<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ClassicPreset, NodeEditor } from 'rete';
import { AreaExtensions, AreaPlugin } from 'rete-area-plugin';
import {
  ClassicFlow,
  ConnectionPlugin,
  getSourceTarget,
  makeConnection,
} from 'rete-connection-plugin';
import { Presets as VuePresets, VuePlugin } from 'rete-vue-plugin';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { templateParametersComplete } from 'dashboard/helper/templateHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import FlowConnection from './components/FlowConnection.vue';
import FlowControl from './components/FlowControl.vue';
import FlowNode from './components/FlowNode.vue';
import FlowSocket from './components/FlowSocket.vue';

const props = defineProps({
  definition: {
    type: Object,
    default: () => ({}),
  },
  inboxId: {
    type: Number,
    default: null,
  },
  agents: {
    type: Array,
    default: () => [],
  },
  labels: {
    type: Array,
    default: () => [],
  },
  publishedFlows: {
    type: Array,
    default: () => [],
  },
  whatsAppTemplates: {
    type: Array,
    default: () => [],
  },
});

const canvasRef = ref(null);
const inspector = ref(null);
const interactiveMediaPreviewUrl = ref('');
const templateParserRef = ref(null);

const editorRef = ref(null);
const areaRef = ref(null);
const socket = new ClassicPreset.Socket('conversation-flow');
const { t, locale } = useI18n();
const inspectorDirection = computed(() =>
  locale.value.startsWith('ar') ? 'rtl' : 'ltr'
);

const nodeDefinitions = {
  noMatch: {
    label: t('CONVERSATION_FLOWS.EDITOR.NO_MATCH.FIXED_TITLE'),
    description: t('CONVERSATION_FLOWS.EDITOR.NO_MATCH.TITLE'),
    icon: 'i-lucide-sparkles',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: false,
    outputs: [{ key: 'next', label: 'Compose next message' }],
  },
  start: {
    label: 'Start Bot Flow',
    description: 'Conversation entry point',
    icon: 'i-lucide-sparkles',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: false,
    outputs: [{ key: 'next', label: 'Compose next message' }],
  },
  text: {
    label: 'Text Message',
    description: 'Send a WhatsApp text body',
    icon: 'i-lucide-pilcrow',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
    message: 'Hello! How can we help?',
  },
  image: {
    label: 'Image Message',
    description: 'Send a WhatsApp image message',
    icon: 'i-lucide-image',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  video: {
    label: 'Video Message',
    description: 'Send a WhatsApp video message',
    icon: 'i-lucide-video',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  audio: {
    label: 'Audio Message',
    description: 'Send a WhatsApp audio message',
    icon: 'i-lucide-volume-2',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  file: {
    label: 'File Message',
    description: 'Send a WhatsApp document message',
    icon: 'i-lucide-file',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  location: {
    label: 'Location Message',
    description: 'Send a WhatsApp location pin',
    icon: 'i-lucide-map-pinned',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  locationRequest: {
    label: 'Request Location',
    description: 'Ask the customer to share a location',
    icon: 'i-lucide-map',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
    message: 'Please share your location to continue.',
  },
  contact: {
    label: 'Contact Message',
    description: 'Send a WhatsApp contact card',
    icon: 'i-lucide-contact-round',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  carousel: {
    label: 'Interactive Carousel',
    description: 'Send a WhatsApp media carousel',
    icon: 'i-lucide-panels-top-left',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [],
    message: 'Browse the options below',
  },
  interactive: {
    label: 'Interactive',
    description: 'Configure the WhatsApp interactive message',
    icon: 'i-lucide-list-tree',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [
      { key: 'next', label: 'Next' },
      { key: 'buttons', label: 'Buttons' },
      { key: 'list', label: 'List message' },
    ],
    message: 'Choose one of the options below',
  },
  button: {
    label: 'Button',
    description: 'WhatsApp reply button option',
    icon: 'i-lucide-square-mouse-pointer',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  listMessage: {
    label: 'List Message',
    description: 'WhatsApp list message option',
    icon: 'i-lucide-list',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  keyboard: {
    label: 'Keyboard',
    description: 'WhatsApp list keyboard',
    icon: 'i-lucide-keyboard',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'sections', label: 'Sections' }],
  },
  section: {
    label: 'Section',
    description: 'WhatsApp list section',
    icon: 'i-lucide-list',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'rows', label: 'Rows' }],
  },
  row: {
    label: 'Row',
    description: 'WhatsApp list row option',
    icon: 'i-lucide-list-tree',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  ai: {
    label: 'AI',
    description: 'Generate a reply with AI',
    icon: 'i-lucide-bot-message-square',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  template: {
    label: 'Message Template',
    description: 'Send an approved WhatsApp message template',
    icon: 'i-lucide-file-text',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  media: {
    label: 'Media',
    description: 'Image, video, audio, or document',
    icon: 'i-lucide-image',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  condition: {
    label: 'Condition',
    description: 'Branch by contact or message data',
    icon: 'i-lucide-git-branch',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [
      { key: 'matched', label: 'Matched' },
      { key: 'fallback', label: 'No match' },
    ],
  },
  action: {
    label: 'Action',
    description: 'Tag, assign, webhook, or update data',
    icon: 'i-lucide-zap',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [{ key: 'next', label: 'Next' }],
  },
  handoff: {
    label: 'Human Handoff',
    description: 'Send the conversation to the team',
    icon: 'i-lucide-hand',
    deleteLabel: t('CONVERSATION_FLOWS.EDITOR.DELETE_NODE'),
    cancelLabel: t('CONVERSATION_FLOWS.EDITOR.CANCEL_NODE_ACTION'),
    input: true,
    outputs: [],
  },
};

const nodeTools = computed(() => [
  {
    type: 'text',
    icon: 'i-lucide-pilcrow',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.TEXT'),
  },
  {
    type: 'image',
    icon: 'i-lucide-image',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.IMAGE'),
  },
  {
    type: 'video',
    icon: 'i-lucide-video',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.VIDEO'),
  },
  {
    type: 'audio',
    icon: 'i-lucide-volume-2',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.AUDIO'),
  },
  {
    type: 'file',
    icon: 'i-lucide-file',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.FILE'),
  },
  {
    type: 'locationRequest',
    icon: 'i-lucide-map',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.LOCATION_REQUEST'),
  },
  {
    type: 'contact',
    icon: 'i-lucide-contact-round',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.CONTACT'),
  },
  {
    type: 'carousel',
    icon: 'i-lucide-panels-top-left',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.CAROUSEL'),
  },
  {
    type: 'interactive',
    icon: 'i-lucide-list-tree',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.INTERACTIVE'),
  },
  {
    type: 'ai',
    icon: 'i-lucide-bot-message-square',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.AI'),
  },
  {
    type: 'template',
    icon: 'i-lucide-file-text',
    label: t('CONVERSATION_FLOWS.EDITOR.NODE_TYPES.TEMPLATE'),
  },
]);

const defaultStartConfig = () => ({
  triggerKeywords: '',
  matchType: 'exact',
  title: '',
  assignedAgentId: '',
  webhookUrl: '',
});

const defaultNoMatchConfig = () => ({
  title: t('CONVERSATION_FLOWS.EDITOR.NO_MATCH.FIXED_TITLE'),
});

const defaultTextConfig = () => ({
  header: '',
  body: '',
  footer: '',
});

const defaultImageConfig = () => ({
  body: '',
  mediaSource: 'uploaded',
  mediaLink: '',
  mediaUrl: '',
  blobId: '',
  blobKey: '',
  contentType: '',
  fileSize: 0,
  fileName: '',
});

const defaultVideoConfig = () => ({
  body: '',
  mediaSource: 'uploaded',
  mediaLink: '',
  mediaUrl: '',
  blobId: '',
  blobKey: '',
  contentType: '',
  fileSize: 0,
  fileName: '',
});

const defaultAudioConfig = () => ({
  mediaSource: 'uploaded',
  mediaLink: '',
  mediaUrl: '',
  blobId: '',
  blobKey: '',
  contentType: '',
  fileSize: 0,
  fileName: '',
});

const defaultFileConfig = () => ({
  body: '',
  mediaSource: 'uploaded',
  mediaLink: '',
  mediaUrl: '',
  blobId: '',
  blobKey: '',
  contentType: '',
  fileSize: 0,
  fileName: '',
});

const defaultTemplateConfig = () => ({
  templateId: '',
  templateName: '',
  namespace: '',
  category: '',
  language: '',
  processedParams: {},
  renderedBody: '',
});

const defaultLocationConfig = () => ({
  latitude: '',
  longitude: '',
  name: '',
  address: '',
});

const defaultLocationRequestConfig = () => ({
  body: '',
});

const defaultCarouselCardConfig = () => ({
  body: '',
  mediaType: 'image',
  mediaSource: 'uploaded',
  mediaLink: '',
  mediaUrl: '',
  blobId: '',
  blobKey: '',
  contentType: '',
  fileSize: 0,
  fileName: '',
  buttonText: '',
  buttonUrl: '',
  quickReplies: [],
});

const defaultCarouselConfig = () => ({
  body: '',
  actionType: 'url',
  cards: [defaultCarouselCardConfig(), defaultCarouselCardConfig()],
});

const defaultAiConfig = () => ({
  instructions: '',
  fallbackMessage: '',
});

const defaultContactPhoneEntry = () => ({
  phone: '',
  type: 'CELL',
  waId: '',
});

const defaultContactEmailEntry = () => ({
  email: '',
  type: 'WORK',
});

const defaultContactUrlEntry = () => ({
  url: '',
  type: 'WORK',
});

const defaultContactAddressEntry = () => ({
  street: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  countryCode: '',
  type: 'HOME',
});

const defaultContactConfig = () => ({
  formattedName: '',
  phoneNumber: '',
  phoneType: 'CELL',
  waId: '',
  phones: [defaultContactPhoneEntry()],
  emails: [defaultContactEmailEntry()],
  urls: [defaultContactUrlEntry()],
  addresses: [defaultContactAddressEntry()],
});

const normalizeContactPhone = value => value?.replace(/[^\d+]/g, '') || '';

const deriveContactWaId = phoneNumber =>
  normalizeContactPhone(phoneNumber).replace(/^\+/, '');

const normalizeLocationCoordinate = value => {
  if (value === '' || value === null || value === undefined) return '';

  const normalizedValue = Number(value);
  return Number.isFinite(normalizedValue) ? normalizedValue : '';
};

const normalizeContactEntries = (entries, fallbackEntry) => {
  if (Array.isArray(entries) && entries.length) {
    return entries.map(entry => ({ ...fallbackEntry(), ...(entry || {}) }));
  }

  return [fallbackEntry()];
};

const normalizeContactConfig = config => {
  const normalizedConfig = {
    ...defaultContactConfig(),
    ...(config || {}),
  };
  const hasConfiguredPhone = Array.isArray(config?.phones)
    ? config.phones.some(item => item?.phone?.trim() || item?.waId?.trim())
    : false;

  const legacyPhones =
    config?.phoneNumber || config?.waId
      ? [
          {
            phone: normalizeContactPhone(config?.phoneNumber || ''),
            type: config?.phoneType || 'CELL',
            waId: config?.waId || deriveContactWaId(config?.phoneNumber || ''),
          },
        ]
      : [];

  normalizedConfig.phones = normalizeContactEntries(
    hasConfiguredPhone ? config.phones : legacyPhones,
    defaultContactPhoneEntry
  );

  normalizedConfig.phones = normalizedConfig.phones.slice(0, 1).map(item => ({
    ...item,
    phone: normalizeContactPhone(item?.phone || ''),
    waId: item?.waId || deriveContactWaId(item?.phone || ''),
    type: item?.type || 'CELL',
  }));

  normalizedConfig.emails = normalizeContactEntries(
    config?.emails?.length ? config.emails : [],
    defaultContactEmailEntry
  ).filter(item => item?.email?.trim());

  normalizedConfig.urls = normalizeContactEntries(
    config?.urls?.length ? config.urls : [],
    defaultContactUrlEntry
  ).filter(item => item?.url?.trim());

  normalizedConfig.addresses = normalizeContactEntries(
    config?.addresses?.length ? config.addresses : [],
    defaultContactAddressEntry
  ).filter(
    item =>
      item?.street?.trim() ||
      item?.city?.trim() ||
      item?.state?.trim() ||
      item?.zip?.trim() ||
      item?.country?.trim() ||
      item?.countryCode?.trim()
  );

  normalizedConfig.phoneNumber = normalizedConfig.phones[0]?.phone || '';
  normalizedConfig.phoneType = normalizedConfig.phones[0]?.type || 'CELL';
  normalizedConfig.waId =
    normalizedConfig.phones[0]?.waId ||
    deriveContactWaId(normalizedConfig.phoneNumber);
  normalizedConfig.firstName =
    config?.firstName ||
    normalizedConfig.formattedName.trim().split(/\s+/).filter(Boolean)[0] ||
    '';

  return normalizedConfig;
};

const defaultInteractiveConfig = () => ({
  body: '',
  footer: '',
  typingOnDisplay: false,
  delay: {
    seconds: 0,
    minutes: 0,
    hours: 0,
  },
  header: {
    enabled: false,
    type: 'text',
    text: '',
    mediaType: 'image',
    mediaSource: 'link',
    mediaLink: '',
    mediaId: '',
    mediaUrl: '',
    blobId: '',
    blobKey: '',
    contentType: '',
    fileSize: 0,
    fileName: '',
  },
});

const defaultButtonConfig = () => ({
  title: '',
  payload: '',
  nextStepType: 'message',
  linkedFlowId: '',
  addLabelIds: [],
  removeLabelIds: [],
});

const defaultListMessageConfig = () => ({
  buttonText: '',
  title: '',
  description: '',
});

const defaultKeyboardConfig = () => ({
  buttonText: '',
});

const defaultSectionConfig = () => ({
  title: '',
});

const defaultRowConfig = () => ({
  title: '',
  payload: '',
  description: '',
});

const normalizeCarouselCard = card => ({
  ...defaultCarouselCardConfig(),
  ...(card || {}),
  quickReplies: Array.isArray(card?.quickReplies)
    ? card.quickReplies.map(reply => ({
        title: reply?.title || '',
      }))
    : [],
});

const normalizeCarouselConfig = config => {
  const cards =
    Array.isArray(config?.cards) && config.cards.length
      ? config.cards.map(normalizeCarouselCard)
      : [defaultCarouselCardConfig(), defaultCarouselCardConfig()];

  return {
    ...defaultCarouselConfig(),
    ...(config || {}),
    cards: cards.slice(0, 10),
  };
};

const childOutputTypes = {
  buttons: 'button',
  list: 'keyboard',
  sections: 'section',
  rows: 'row',
};

const executableNodeTypes = [
  'text',
  'interactive',
  'image',
  'video',
  'audio',
  'file',
  'location',
  'locationRequest',
  'contact',
  'carousel',
  'ai',
  'media',
  'template',
];

const structuralNodeTypes = ['start', 'noMatch', 'keyboard', 'section', 'row'];

const sourceOutputTargetRules = {
  buttons: ['button'],
  list: ['keyboard'],
  sections: ['section'],
  rows: ['row'],
};

const mediaTypeFromMime = mime => {
  if (mime?.startsWith('image/')) return 'image';
  if (mime?.startsWith('video/')) return 'video';

  return 'document';
};

const supportedAudioExtensions = [
  '.aac',
  '.amr',
  '.m4a',
  '.mp3',
  '.mp4',
  '.mpeg',
  '.oga',
  '.ogg',
  '.opus',
];

const isSupportedAudioFile = file => {
  const mime = file.type?.toLowerCase() || '';
  const name = file.name?.toLowerCase() || '';

  if (
    [
      'audio/aac',
      'audio/amr',
      'audio/mpeg',
      'audio/mp4',
      'audio/ogg',
      'audio/opus',
    ].includes(mime)
  ) {
    return true;
  }

  return supportedAudioExtensions.some(extension => name.endsWith(extension));
};

const maxInteractiveHeaderMediaSize = file => {
  if (file.type?.startsWith('image/')) return 5;
  if (file.type?.startsWith('video/')) return 16;

  return 100;
};

const revokeInteractiveMediaPreviewUrl = () => {
  if (!interactiveMediaPreviewUrl.value?.startsWith('blob:')) return;
  URL.revokeObjectURL(interactiveMediaPreviewUrl.value);
};

const clearNodeMediaConfig = (nodeType, existingConfig = {}) => {
  if (nodeType === 'image') {
    return {
      ...defaultImageConfig(),
      body: existingConfig.body || '',
    };
  }

  if (nodeType === 'video') {
    return {
      ...defaultVideoConfig(),
      body: existingConfig.body || '',
    };
  }

  if (nodeType === 'audio') {
    return defaultAudioConfig();
  }

  if (nodeType === 'file') {
    return {
      ...defaultFileConfig(),
      body: existingConfig.body || '',
    };
  }

  return existingConfig;
};

const resetInspectorNodeMedia = nodeType => {
  if (!inspector.value || inspector.value.type !== nodeType) return;

  revokeInteractiveMediaPreviewUrl();
  interactiveMediaPreviewUrl.value = '';
  inspector.value.config = clearNodeMediaConfig(
    nodeType,
    inspector.value.config || {}
  );
};

const replaceInspectorNodeMedia = nodeType => {
  if (inspector.value?.type !== nodeType) return;

  const input = document.getElementById(`conversation-flow-${nodeType}-upload`);
  input?.click();
};

const createNode = nodeDefinition => {
  const config = nodeDefinitions[nodeDefinition.type] || nodeDefinitions.text;
  const nodeType = nodeDefinition.type || 'text';
  const nodeConfig =
    nodeType === 'contact'
      ? normalizeContactConfig(nodeDefinition.config || {})
      : nodeDefinition.config || {};
  const node = new ClassicPreset.Node(nodeDefinition.label || config.label);
  node.width = 260;
  node.height = 150;
  node.data = {
    type: nodeType,
    description: nodeDefinition.description || config.description,
    icon: nodeDefinition.icon || config.icon,
    deleteLabel: config.deleteLabel,
    cancelLabel: config.cancelLabel,
    config: {
      ...(nodeType === 'noMatch' ? defaultNoMatchConfig() : {}),
      ...(nodeType === 'start' ? defaultStartConfig() : {}),
      ...(nodeType === 'text' ? defaultTextConfig() : {}),
      ...(nodeType === 'image' ? defaultImageConfig() : {}),
      ...(nodeType === 'video' ? defaultVideoConfig() : {}),
      ...(nodeType === 'audio' ? defaultAudioConfig() : {}),
      ...(nodeType === 'file' ? defaultFileConfig() : {}),
      ...(nodeType === 'location' ? defaultLocationConfig() : {}),
      ...(nodeType === 'locationRequest' ? defaultLocationRequestConfig() : {}),
      ...(nodeType === 'carousel' ? defaultCarouselConfig() : {}),
      ...(nodeType === 'ai' ? defaultAiConfig() : {}),
      ...(nodeType === 'contact' ? defaultContactConfig() : {}),
      ...(nodeType === 'template' ? defaultTemplateConfig() : {}),
      ...(nodeType === 'interactive' ? defaultInteractiveConfig() : {}),
      ...(nodeType === 'button' ? defaultButtonConfig() : {}),
      ...(nodeType === 'listMessage' ? defaultListMessageConfig() : {}),
      ...(nodeType === 'keyboard' ? defaultKeyboardConfig() : {}),
      ...(nodeType === 'section' ? defaultSectionConfig() : {}),
      ...(nodeType === 'row' ? defaultRowConfig() : {}),
      ...nodeConfig,
    },
    summary:
      nodeDefinition.summary ||
      nodeConfig?.title ||
      nodeConfig?.triggerKeywords ||
      nodeConfig?.body ||
      nodeConfig?.title ||
      nodeConfig?.buttonText ||
      nodeConfig?.templateName ||
      nodeConfig?.renderedBody ||
      nodeConfig?.header?.text ||
      nodeConfig?.header?.mediaLink ||
      nodeDefinition.message ||
      nodeDefinition.condition ||
      config.message ||
      config.description,
  };

  if (nodeDefinition.id) node.id = nodeDefinition.id;
  if (config.input) {
    node.addInput('reply', new ClassicPreset.Input(socket, 'Reply'));
  }
  config.outputs.forEach(output => {
    node.addOutput(output.key, new ClassicPreset.Output(socket, output.label));
  });

  if (
    ['text', 'interactive', 'locationRequest', 'carousel'].includes(
      node.data.type
    )
  ) {
    node.addControl(
      'message',
      new ClassicPreset.InputControl('text', {
        initial:
          nodeDefinition.config?.body ||
          nodeDefinition.message ||
          config.message,
      })
    );
  }

  if (node.data.type === 'condition') {
    node.addControl(
      'condition',
      new ClassicPreset.InputControl('text', {
        initial: nodeDefinition.condition || 'message.body contains',
      })
    );
  }

  return node;
};

const addEditorNode = async (nodeDefinition, nodeMap) => {
  const editor = editorRef.value;
  const area = areaRef.value;
  const node = createNode(nodeDefinition);
  nodeMap.set(node.id, node);
  await editor.addNode(node);
  await area.translate(node.id, nodeDefinition.position || { x: 80, y: 80 });
};

const getEntryNode = () =>
  editorRef.value
    ?.getNodes()
    .find(node => ['start', 'noMatch'].includes(node.data?.type));

const addEditorConnection = async (connectionDefinition, nodeMap) => {
  const editor = editorRef.value;
  const source = nodeMap.get(connectionDefinition.source);
  const target = nodeMap.get(connectionDefinition.target);
  if (!source || !target) return;

  const connection = new ClassicPreset.Connection(
    source,
    connectionDefinition.sourceOutput || 'next',
    target,
    connectionDefinition.targetInput || 'reply'
  );
  if (connectionDefinition.id) connection.id = connectionDefinition.id;
  await editor.addConnection(connection);
};

const getNodePosition = nodeId => {
  return areaRef.value?.nodeViews.get(nodeId)?.position || { x: 0, y: 0 };
};

const connectedChildCount = (nodeId, outputKey) => {
  return editorRef.value
    .getConnections()
    .filter(
      connection =>
        connection.source === nodeId && connection.sourceOutput === outputKey
    ).length;
};

const connectedButtonCount = nodeId => connectedChildCount(nodeId, 'buttons');

const buttonLimitForNode = node => {
  if (node?.data?.type === 'carousel') return 2;
  return 3;
};

const connectedListMessageCount = nodeId => connectedChildCount(nodeId, 'list');

const isMediaHeaderInteractive = node =>
  node?.data?.type === 'interactive' &&
  node.data?.config?.header?.enabled &&
  node.data?.config?.header?.type === 'media';

const getConnectedChildTypes = (nodeId, outputKey) => {
  const editor = editorRef.value;
  if (!editor) return [];

  return editor
    .getConnections()
    .filter(
      connection =>
        connection.source === nodeId && connection.sourceOutput === outputKey
    )
    .map(connection => editor.getNode(connection.target)?.data?.type)
    .filter(Boolean);
};

const hasConnectedChildType = (nodeId, outputKey, types) =>
  getConnectedChildTypes(nodeId, outputKey).some(type => types.includes(type));

const hasInteractiveButtons = nodeId =>
  hasConnectedChildType(nodeId, 'buttons', ['button']);

const hasInteractiveList = nodeId =>
  hasConnectedChildType(nodeId, 'list', ['keyboard', 'listMessage']);

const isButtonFlowMode = node =>
  node?.data?.type === 'button' && node.data?.config?.nextStepType === 'flow';

const buildButtonPayload = ({ nodeId, title, existingPayload }) => {
  if (existingPayload) return existingPayload;

  const normalizedTitle = title
    ?.trim()
    ?.toLowerCase()
    ?.replace(/[^a-z0-9]+/g, '_')
    ?.replace(/^_+|_+$/g, '');

  return normalizedTitle
    ? `button_${normalizedTitle}_${nodeId}`
    : `button_${nodeId}`;
};

const addChildNodeFromOutput = async (sourceNodeId, outputKey) => {
  const editor = editorRef.value;
  const area = areaRef.value;
  const sourceNode = editor?.getNode(sourceNodeId);
  if (!editor || !area || !sourceNode) return;

  const type = childOutputTypes[outputKey];
  if (!type) return;
  if (!sourceNode.outputs[outputKey]) return;

  if (
    outputKey === 'buttons' &&
    connectedButtonCount(sourceNodeId) >= buttonLimitForNode(sourceNode)
  ) {
    return;
  }
  if (outputKey === 'buttons' && hasInteractiveList(sourceNodeId)) return;
  if (outputKey === 'list' && hasInteractiveButtons(sourceNodeId)) return;
  if (outputKey === 'list' && connectedListMessageCount(sourceNodeId) >= 1) {
    return;
  }
  if (outputKey === 'list' && isMediaHeaderInteractive(sourceNode)) return;

  const sourcePosition = getNodePosition(sourceNodeId);
  const childIndex = connectedChildCount(sourceNodeId, outputKey);
  const childNode = createNode({ type });
  await editor.addNode(childNode);
  await area.translate(childNode.id, {
    x: sourcePosition.x + 360,
    y: sourcePosition.y + childIndex * 170,
  });
  await editor.addConnection(
    new ClassicPreset.Connection(sourceNode, outputKey, childNode, 'reply')
  );
};

const isExecutableNode = node =>
  executableNodeTypes.includes(node?.data?.type || '');

const isValidSourceTarget = (source, target) => {
  const sourceNode = editorRef.value?.getNode(source.nodeId);
  const targetNode = editorRef.value?.getNode(target.nodeId);
  const sourceType = sourceNode?.data?.type;
  const targetType = targetNode?.data?.type;

  if (!sourceType || !targetType) return false;
  if (source.nodeId === target.nodeId) return false;

  const requiredTargetTypes = sourceOutputTargetRules[source.key];
  if (requiredTargetTypes) {
    if (!requiredTargetTypes.includes(targetType)) return false;
    if (source.key === 'list' && isMediaHeaderInteractive(sourceNode)) {
      return false;
    }
    if (
      source.key === 'buttons' &&
      connectedButtonCount(source.nodeId) >= buttonLimitForNode(sourceNode)
    ) {
      return false;
    }
    if (
      source.key === 'list' &&
      connectedListMessageCount(source.nodeId) >= 1
    ) {
      return false;
    }
    if (source.key === 'buttons' && hasInteractiveList(source.nodeId)) {
      return false;
    }
    if (source.key === 'list' && hasInteractiveButtons(source.nodeId)) {
      return false;
    }

    return true;
  }

  if (['button', 'row'].includes(sourceType)) {
    if (sourceType === 'button' && isButtonFlowMode(sourceNode)) {
      return false;
    }
    return isExecutableNode(targetNode);
  }

  if (sourceType === 'start') {
    return isExecutableNode(targetNode);
  }

  if (sourceType === 'noMatch') {
    return isExecutableNode(targetNode);
  }

  if (structuralNodeTypes.includes(targetType)) return false;

  return isExecutableNode(targetNode);
};

const canMakeFlowConnection = (initial, nextSocket) => {
  const [source, target] = getSourceTarget(initial, nextSocket) || [];
  if (!source || !target) return false;

  return isValidSourceTarget(source, target);
};

const makeFlowConnection = (initial, nextSocket, context) => {
  if (!canMakeFlowConnection(initial, nextSocket)) return false;

  return makeConnection(initial, nextSocket, context);
};

const getNodeMessage = node => {
  if (
    ['text', 'interactive', 'locationRequest', 'carousel'].includes(
      node.data?.type
    )
  ) {
    return node.data?.config?.body || '';
  }

  return node.controls?.message?.value || '';
};

const normalizedNodeConfig = node => {
  if (node.data?.type === 'carousel') {
    return normalizeCarouselConfig(node.data?.config || {});
  }

  if (node.data?.type === 'contact') {
    return normalizeContactConfig(node.data?.config || {});
  }

  return node.data?.config || {};
};

function legacyCarouselQuickReplies(nodeId) {
  const editor = editorRef.value;
  if (!editor) return [];

  return editor
    .getConnections()
    .filter(
      connection =>
        connection.source === nodeId && connection.sourceOutput === 'buttons'
    )
    .map(connection => editor.getNode(connection.target))
    .filter(Boolean)
    .map(buttonNode => ({
      title: buttonNode?.data?.config?.title || '',
    }))
    .filter(reply => reply.title)
    .slice(0, 2);
}

const serializeNode = node => {
  const position = getNodePosition(node.id);
  const normalizedConfig = normalizedNodeConfig(node);

  return {
    id: node.id,
    type: node.data?.type || 'text',
    label: node.label,
    message: getNodeMessage(node),
    condition: node.controls?.condition?.value || '',
    summary: node.data?.summary || '',
    config: normalizedConfig,
    position,
  };
};

const getDefinition = () => {
  const editor = editorRef.value;
  if (!editor) return props.definition;

  return {
    version: 1,
    nodes: editor.getNodes().map(serializeNode),
    connections: editor.getConnections().map(connection => ({
      id: connection.id,
      source: connection.source,
      sourceOutput: connection.sourceOutput,
      target: connection.target,
      targetInput: connection.targetInput,
    })),
  };
};

const getFlowName = () =>
  getEntryNode()?.label || t('CONVERSATION_FLOWS.DEFAULT_NAME');

const isValidWebhookUrl = value => {
  if (!value) return true;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol);
  } catch (error) {
    return false;
  }
};

const fitView = () => {
  const editor = editorRef.value;
  const area = areaRef.value;
  if (!editor || !area) return;
  AreaExtensions.zoomAt(area, editor.getNodes());
};

const addNode = async (type, positionOverride = null) => {
  const editor = editorRef.value;
  const area = areaRef.value;
  if (!editor || !area) return;
  if (['button', 'listMessage', 'keyboard', 'section', 'row'].includes(type)) {
    return;
  }
  if (
    type === 'start' &&
    editor.getNodes().some(node => node.data?.type === 'start')
  ) {
    return;
  }
  if (
    type === 'noMatch' &&
    editor
      .getNodes()
      .some(node => ['start', 'noMatch'].includes(node.data?.type))
  ) {
    return;
  }

  const position = positionOverride || {
    x: type === 'start' ? 80 : 360 + editor.getNodes().length * 24,
    y: 140 + editor.getNodes().length * 48,
  };
  const node = createNode({
    type,
  });

  await editor.addNode(node);
  await area.translate(node.id, position);
};

const addToolNode = type => {
  addNode(type);
};

const handleToolDragStart = (event, type) => {
  event.dataTransfer.effectAllowed = 'copy';
  event.dataTransfer.setData('application/x-conversation-flow-node', type);
};

const getCanvasDropPosition = event => {
  const area = areaRef.value?.area;
  const rect = canvasRef.value.getBoundingClientRect();
  const transform = area?.transform || { x: 0, y: 0, k: 1 };

  return {
    x: (event.clientX - rect.left - transform.x) / transform.k,
    y: (event.clientY - rect.top - transform.y) / transform.k,
  };
};

const handleCanvasDrop = event => {
  const type = event.dataTransfer.getData(
    'application/x-conversation-flow-node'
  );
  if (!type) return;

  addNode(type, getCanvasDropPosition(event));
};

const deleteNode = async nodeId => {
  const editor = editorRef.value;
  if (!editor) return;

  const node = editor.getNode(nodeId);
  if (!node || node.data?.type === 'start') return;

  const connections = editor
    .getConnections()
    .filter(
      connection => connection.source === nodeId || connection.target === nodeId
    );

  await connections.reduce(
    (promise, connection) =>
      promise.then(() => editor.removeConnection(connection.id)),
    Promise.resolve()
  );
  await editor.removeNode(nodeId);
};

const handleNodeDelete = event => {
  deleteNode(event.detail?.nodeId);
};

const handleNodeAddChild = event => {
  addChildNodeFromOutput(event.detail?.nodeId, event.detail?.outputKey);
};

const openNodeInspector = nodeId => {
  const node = editorRef.value?.getNode(nodeId);
  if (!node) return;

  inspector.value = {
    id: node.id,
    type: node.data?.type || 'text',
    label: node.label,
    message: node.controls?.message?.value || '',
    condition: node.controls?.condition?.value || '',
    addLabelDraft: '',
    removeLabelDraft: '',
    config: {
      ...(node.data?.type === 'noMatch' ? defaultNoMatchConfig() : {}),
      ...(node.data?.type === 'start' ? defaultStartConfig() : {}),
      ...(node.data?.type === 'text' ? defaultTextConfig() : {}),
      ...(node.data?.type === 'image' ? defaultImageConfig() : {}),
      ...(node.data?.type === 'video' ? defaultVideoConfig() : {}),
      ...(node.data?.type === 'audio' ? defaultAudioConfig() : {}),
      ...(node.data?.type === 'file' ? defaultFileConfig() : {}),
      ...(node.data?.type === 'location' ? defaultLocationConfig() : {}),
      ...(node.data?.type === 'locationRequest'
        ? defaultLocationRequestConfig()
        : {}),
      ...(node.data?.type === 'carousel' ? defaultCarouselConfig() : {}),
      ...(node.data?.type === 'ai' ? defaultAiConfig() : {}),
      ...(node.data?.type === 'contact' ? defaultContactConfig() : {}),
      ...(node.data?.type === 'template' ? defaultTemplateConfig() : {}),
      ...(node.data?.type === 'interactive' ? defaultInteractiveConfig() : {}),
      ...(node.data?.type === 'button' ? defaultButtonConfig() : {}),
      ...(node.data?.type === 'listMessage' ? defaultListMessageConfig() : {}),
      ...(node.data?.type === 'keyboard' ? defaultKeyboardConfig() : {}),
      ...(node.data?.type === 'section' ? defaultSectionConfig() : {}),
      ...(node.data?.type === 'row' ? defaultRowConfig() : {}),
      ...(node.data?.config || {}),
    },
  };
  if (node.data?.type === 'carousel') {
    inspector.value.config = normalizeCarouselConfig(inspector.value.config);
    if (inspector.value.config.actionType === 'quickReply') {
      const hasQuickReplies = inspector.value.config.cards.some(
        card => (card.quickReplies || []).length
      );

      if (!hasQuickReplies) {
        const legacyReplies = legacyCarouselQuickReplies(nodeId);
        if (legacyReplies.length) {
          inspector.value.config.cards = inspector.value.config.cards.map(
            card => ({
              ...card,
              quickReplies: legacyReplies.map(reply => ({ ...reply })),
            })
          );
        }
      }
    }
  }
  if (node.data?.type === 'contact') {
    inspector.value.config = normalizeContactConfig(inspector.value.config);
  }
  revokeInteractiveMediaPreviewUrl();
  interactiveMediaPreviewUrl.value =
    node.data?.config?.mediaUrl ||
    node.data?.config?.mediaLink ||
    node.data?.config?.header?.mediaUrl ||
    node.data?.config?.header?.mediaLink ||
    '';
};

const uploadInteractiveHeaderMedia = async event => {
  const file = event.target.files?.[0];
  if (!file || inspector.value?.type !== 'interactive') return;

  const maxSize = maxInteractiveHeaderMediaSize(file);
  if (file.size > maxSize * 1024 * 1024) {
    useAlert(
      t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.MEDIA_SIZE_ERROR', {
        size: maxSize,
      })
    );
    event.target.value = '';
    return;
  }

  revokeInteractiveMediaPreviewUrl();
  interactiveMediaPreviewUrl.value = URL.createObjectURL(file);

  try {
    const uploadedFile = await uploadFile(file);
    inspector.value.config.header = {
      ...inspector.value.config.header,
      enabled: true,
      type: 'media',
      mediaType: mediaTypeFromMime(file.type),
      mediaSource: 'uploaded',
      mediaUrl: uploadedFile.fileUrl,
      blobId: uploadedFile.blobId,
      blobKey: uploadedFile.blobKey,
      contentType: file.type,
      fileSize: file.size,
      fileName: file.name,
    };
  } catch (error) {
    useAlert(error.message || t('CONVERSATION_FLOWS.EDITOR.SAVE_ERROR'));
  } finally {
    event.target.value = '';
  }
};

const uploadImageMedia = async event => {
  const file = event.target.files?.[0];
  if (!file || inspector.value?.type !== 'image') return;

  if (!file.type?.startsWith('image/')) {
    useAlert(t('CONVERSATION_FLOWS.EDITOR.IMAGE.INVALID_FILE'));
    event.target.value = '';
    return;
  }

  const maxSize = 5;
  if (file.size > maxSize * 1024 * 1024) {
    useAlert(
      t('CONVERSATION_FLOWS.EDITOR.IMAGE.SIZE_ERROR', { size: maxSize })
    );
    event.target.value = '';
    return;
  }

  revokeInteractiveMediaPreviewUrl();
  interactiveMediaPreviewUrl.value = URL.createObjectURL(file);

  try {
    const uploadedFile = await uploadFile(file);
    inspector.value.config = {
      ...inspector.value.config,
      mediaSource: 'uploaded',
      mediaLink: '',
      mediaUrl: uploadedFile.fileUrl,
      blobId: uploadedFile.blobId,
      blobKey: uploadedFile.blobKey,
      contentType: file.type,
      fileSize: file.size,
      fileName: file.name,
    };
  } catch (error) {
    useAlert(error.message || t('CONVERSATION_FLOWS.EDITOR.SAVE_ERROR'));
  } finally {
    event.target.value = '';
  }
};

const uploadVideoMedia = async event => {
  const file = event.target.files?.[0];
  if (!file || inspector.value?.type !== 'video') return;

  if (!file.type?.startsWith('video/')) {
    useAlert(t('CONVERSATION_FLOWS.EDITOR.VIDEO.INVALID_FILE'));
    event.target.value = '';
    return;
  }

  const maxSize = 16;
  if (file.size > maxSize * 1024 * 1024) {
    useAlert(
      t('CONVERSATION_FLOWS.EDITOR.VIDEO.SIZE_ERROR', { size: maxSize })
    );
    event.target.value = '';
    return;
  }

  revokeInteractiveMediaPreviewUrl();
  interactiveMediaPreviewUrl.value = URL.createObjectURL(file);

  try {
    const uploadedFile = await uploadFile(file);
    inspector.value.config = {
      ...inspector.value.config,
      mediaSource: 'uploaded',
      mediaLink: '',
      mediaUrl: uploadedFile.fileUrl,
      blobId: uploadedFile.blobId,
      blobKey: uploadedFile.blobKey,
      contentType: file.type,
      fileSize: file.size,
      fileName: file.name,
    };
  } catch (error) {
    useAlert(error.message || t('CONVERSATION_FLOWS.EDITOR.SAVE_ERROR'));
  } finally {
    event.target.value = '';
  }
};

const uploadAudioMedia = async event => {
  const file = event.target.files?.[0];
  if (!file || inspector.value?.type !== 'audio') return;

  if (!isSupportedAudioFile(file)) {
    useAlert(t('CONVERSATION_FLOWS.EDITOR.AUDIO.UNSUPPORTED_FILE'));
    event.target.value = '';
    return;
  }

  const maxSize = 16;
  if (file.size > maxSize * 1024 * 1024) {
    useAlert(
      t('CONVERSATION_FLOWS.EDITOR.AUDIO.SIZE_ERROR', { size: maxSize })
    );
    event.target.value = '';
    return;
  }

  revokeInteractiveMediaPreviewUrl();
  interactiveMediaPreviewUrl.value = URL.createObjectURL(file);

  try {
    const uploadedFile = await uploadFile(file);
    inspector.value.config = {
      ...inspector.value.config,
      mediaSource: 'uploaded',
      mediaLink: '',
      mediaUrl: uploadedFile.fileUrl,
      blobId: uploadedFile.blobId,
      blobKey: uploadedFile.blobKey,
      contentType: file.type,
      fileSize: file.size,
      fileName: file.name,
    };
  } catch (error) {
    useAlert(error.message || t('CONVERSATION_FLOWS.EDITOR.SAVE_ERROR'));
  } finally {
    event.target.value = '';
  }
};

const uploadFileMedia = async event => {
  const file = event.target.files?.[0];
  if (!file || inspector.value?.type !== 'file') return;

  const maxSize = 100;
  if (file.size > maxSize * 1024 * 1024) {
    useAlert(t('CONVERSATION_FLOWS.EDITOR.FILE.SIZE_ERROR', { size: maxSize }));
    event.target.value = '';
    return;
  }

  revokeInteractiveMediaPreviewUrl();
  interactiveMediaPreviewUrl.value = '';

  try {
    const uploadedFile = await uploadFile(file);
    inspector.value.config = {
      ...inspector.value.config,
      mediaSource: 'uploaded',
      mediaLink: '',
      mediaUrl: uploadedFile.fileUrl,
      blobId: uploadedFile.blobId,
      blobKey: uploadedFile.blobKey,
      contentType: file.type,
      fileSize: file.size,
      fileName: file.name,
    };
  } catch (error) {
    useAlert(error.message || t('CONVERSATION_FLOWS.EDITOR.SAVE_ERROR'));
  } finally {
    event.target.value = '';
  }
};

const addCarouselCard = () => {
  if (inspector.value?.type !== 'carousel') return;
  if (inspector.value.config.cards.length >= 10) return;

  inspector.value.config.cards.push(defaultCarouselCardConfig());
};

const removeCarouselCard = index => {
  if (inspector.value?.type !== 'carousel') return;
  if (inspector.value.config.cards.length <= 2) return;

  inspector.value.config.cards.splice(index, 1);
};

const uploadCarouselCardMedia = async (event, index) => {
  const file = event.target.files?.[0];
  if (!file || inspector.value?.type !== 'carousel') return;

  const isImage = file.type?.startsWith('image/');
  const isVideo = file.type?.startsWith('video/');
  if (!isImage && !isVideo) {
    useAlert(t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.INVALID_FILE'));
    event.target.value = '';
    return;
  }

  const maxSize = isImage ? 5 : 16;
  if (file.size > maxSize * 1024 * 1024) {
    useAlert(
      t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.MEDIA_SIZE_ERROR', {
        size: maxSize,
      })
    );
    event.target.value = '';
    return;
  }

  try {
    const uploadedFile = await uploadFile(file);
    inspector.value.config.cards[index] = {
      ...normalizeCarouselCard(inspector.value.config.cards[index]),
      mediaType: isImage ? 'image' : 'video',
      mediaSource: 'uploaded',
      mediaLink: '',
      mediaUrl: uploadedFile.fileUrl,
      blobId: uploadedFile.blobId,
      blobKey: uploadedFile.blobKey,
      contentType: file.type,
      fileSize: file.size,
      fileName: file.name,
    };
  } catch (error) {
    useAlert(error.message || t('CONVERSATION_FLOWS.EDITOR.SAVE_ERROR'));
  } finally {
    event.target.value = '';
  }
};

const replaceCarouselCardMedia = index => {
  document
    .getElementById(`conversation-flow-carousel-upload-${index}`)
    ?.click();
};

const clearCarouselCardMedia = index => {
  if (inspector.value?.type !== 'carousel') return;

  inspector.value.config.cards[index] = {
    ...normalizeCarouselCard(inspector.value.config.cards[index]),
    mediaLink: '',
    mediaUrl: '',
    blobId: '',
    blobKey: '',
    contentType: '',
    fileSize: 0,
    fileName: '',
  };
};

const addCarouselQuickReply = cardIndex => {
  if (inspector.value?.type !== 'carousel') return;

  const card = inspector.value.config.cards[cardIndex];
  if (!card || (card.quickReplies || []).length >= 2) return;

  card.quickReplies = [...(card.quickReplies || []), { title: '' }];
};

const removeCarouselQuickReply = (cardIndex, replyIndex) => {
  if (inspector.value?.type !== 'carousel') return;

  const card = inspector.value.config.cards[cardIndex];
  if (!card) return;

  card.quickReplies = (card.quickReplies || []).filter(
    (_reply, index) => index !== replyIndex
  );
};

const selectedFlowName = flowId => {
  const flow = props.publishedFlows.find(item => item.id === Number(flowId));
  return flow?.name || '';
};

const selectedTemplate = templateId => {
  return props.whatsAppTemplates.find(item => item.id === Number(templateId));
};

const selectedInspectorTemplate = computed(() =>
  selectedTemplate(inspector.value?.config?.templateId)
);

const selectedLabelTitles = labelIds => {
  if (!Array.isArray(labelIds) || !labelIds.length) return [];

  return labelIds
    .map(
      labelId => props.labels.find(item => item.id === Number(labelId))?.title
    )
    .filter(Boolean);
};

const normalizeMultiValue = values =>
  Array.isArray(values)
    ? values.map(value => Number(value)).filter(Boolean)
    : [];

const selectedLabelRecords = labelIds => {
  const normalizedIds = normalizeMultiValue(labelIds);
  return normalizedIds
    .map(labelId => props.labels.find(item => item.id === Number(labelId)))
    .filter(Boolean);
};

const availableLabelsFor = labelIds => {
  const selectedIds = normalizeMultiValue(labelIds);
  return props.labels.filter(label => !selectedIds.includes(label.id));
};

const updateButtonInspectorMode = nextStepType => {
  if (inspector.value?.type !== 'button') return;

  inspector.value.config.nextStepType = nextStepType;
  if (nextStepType === 'message') {
    inspector.value.config.linkedFlowId = '';
    return;
  }

  inspector.value.config.title = '';
  inspector.value.config.addLabelIds = [];
  inspector.value.config.removeLabelIds = [];
  inspector.value.addLabelDraft = '';
  inspector.value.removeLabelDraft = '';
};

const defaultNodeSummary = currentInspector =>
  (currentInspector.type === 'carousel'
    ? [
        currentInspector.config?.body,
        `${currentInspector.config?.cards?.length || 0} cards`,
        currentInspector.config?.actionType === 'quickReply'
          ? 'Quick replies'
          : 'URL',
      ]
        .filter(Boolean)
        .join(' · ')
    : null) ||
  (currentInspector.type === 'location'
    ? [
        currentInspector.config?.name,
        [currentInspector.config?.latitude, currentInspector.config?.longitude]
          .filter(Boolean)
          .join(', '),
      ]
        .filter(Boolean)
        .join(' · ')
    : null) ||
  (currentInspector.type === 'contact'
    ? [
        currentInspector.config?.formattedName,
        [currentInspector.config?.firstName, currentInspector.config?.lastName]
          .filter(Boolean)
          .join(' '),
        currentInspector.config?.phones?.find(item => item?.phone?.trim())
          ?.phone || currentInspector.config?.phoneNumber,
      ]
        .filter(Boolean)
        .join(' · ')
    : null) ||
  currentInspector.config?.title ||
  currentInspector.config?.triggerKeywords ||
  currentInspector.config?.body ||
  currentInspector.config?.templateName ||
  currentInspector.config?.renderedBody ||
  currentInspector.config?.title ||
  currentInspector.config?.fileName ||
  currentInspector.config?.mediaLink ||
  currentInspector.config?.payload ||
  currentInspector.config?.buttonText ||
  currentInspector.config?.header?.text ||
  currentInspector.config?.header?.mediaLink ||
  currentInspector.config?.header?.mediaId ||
  currentInspector.message ||
  currentInspector.condition ||
  '';

const startNodeSummary = currentInspector =>
  [
    currentInspector.config?.triggerKeywords,
    currentInspector.config?.webhookUrl,
  ]
    .filter(Boolean)
    .join(' · ');

const buttonNodeSummary = currentInspector =>
  [
    currentInspector.config?.nextStepType === 'flow'
      ? selectedFlowName(currentInspector.config?.linkedFlowId)
      : currentInspector.config?.title,
    ...selectedLabelTitles(currentInspector.config?.addLabelIds),
    ...selectedLabelTitles(currentInspector.config?.removeLabelIds),
  ]
    .filter(Boolean)
    .join(' · ');

const templateNodeSummary = currentInspector =>
  [currentInspector.config?.templateName, currentInspector.config?.renderedBody]
    .filter(Boolean)
    .join(' · ');

const addButtonLabel = key => {
  if (inspector.value?.type !== 'button') return;

  const draftKey = key === 'add' ? 'addLabelDraft' : 'removeLabelDraft';
  const configKey = key === 'add' ? 'addLabelIds' : 'removeLabelIds';
  const nextLabelId = Number(inspector.value[draftKey]);
  if (!nextLabelId) return;

  const currentLabelIds = normalizeMultiValue(
    inspector.value.config?.[configKey]
  );
  if (currentLabelIds.includes(nextLabelId)) {
    inspector.value[draftKey] = '';
    return;
  }

  inspector.value.config[configKey] = [...currentLabelIds, nextLabelId];
  inspector.value[draftKey] = '';
};

const removeButtonLabel = (key, labelId) => {
  if (inspector.value?.type !== 'button') return;

  const configKey = key === 'add' ? 'addLabelIds' : 'removeLabelIds';
  inspector.value.config[configKey] = normalizeMultiValue(
    inspector.value.config?.[configKey]
  ).filter(currentLabelId => currentLabelId !== Number(labelId));
};

const handleTemplateSelection = template => {
  if (inspector.value?.type !== 'template' || !template) return;

  const sameTemplate =
    Number(inspector.value.config?.templateId) === Number(template.id);

  inspector.value.config = {
    ...defaultTemplateConfig(),
    ...inspector.value.config,
    templateId: template.id,
    templateName: template.name || '',
    namespace: template.namespace || '',
    category: template.category || '',
    language: template.language || '',
    processedParams: sameTemplate
      ? inspector.value.config?.processedParams || {}
      : {},
    renderedBody: sameTemplate
      ? inspector.value.config?.renderedBody || ''
      : '',
  };
};

const handleNodeEdit = event => {
  openNodeInspector(event.detail?.nodeId);
};

const closeInspector = () => {
  inspector.value = null;
};

const validateInspectorConfig = currentInspector => {
  if (!currentInspector) return '';

  if (currentInspector.type === 'noMatch') return '';

  if (
    currentInspector.type === 'start' &&
    !currentInspector.config?.title?.trim()
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.START_TITLE_REQUIRED');
  }

  if (
    currentInspector.type === 'start' &&
    !isValidWebhookUrl(currentInspector.config?.webhookUrl)
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.START.WEBHOOK_ERROR');
  }

  if (
    currentInspector.type === 'text' &&
    !currentInspector.config?.body?.trim()
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.TEXT_BODY_REQUIRED');
  }

  if (
    currentInspector.type === 'image' &&
    !currentInspector.config?.blobId &&
    !currentInspector.config?.mediaLink?.trim()
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.IMAGE_MEDIA_REQUIRED');
  }

  if (
    currentInspector.type === 'video' &&
    !currentInspector.config?.blobId &&
    !currentInspector.config?.mediaLink?.trim()
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.VIDEO_MEDIA_REQUIRED');
  }

  if (
    currentInspector.type === 'audio' &&
    !currentInspector.config?.blobId &&
    !currentInspector.config?.mediaLink?.trim()
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.AUDIO_MEDIA_REQUIRED');
  }

  if (
    currentInspector.type === 'file' &&
    !currentInspector.config?.blobId &&
    !currentInspector.config?.mediaLink?.trim()
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.FILE_MEDIA_REQUIRED');
  }

  if (
    currentInspector.type === 'interactive' &&
    !currentInspector.config?.body?.trim()
  ) {
    return t(
      'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.INTERACTIVE_BODY_REQUIRED'
    );
  }

  if (
    currentInspector.type === 'button' &&
    currentInspector.config?.nextStepType === 'flow' &&
    !currentInspector.config?.linkedFlowId
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.BUTTON_FLOW_REQUIRED');
  }

  if (
    currentInspector.type === 'button' &&
    currentInspector.config?.nextStepType !== 'flow' &&
    !currentInspector.config?.title?.trim()
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.BUTTON_TITLE_REQUIRED');
  }

  if (
    currentInspector.type === 'row' &&
    !currentInspector.config?.title?.trim()
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_TITLE_REQUIRED');
  }

  if (
    currentInspector.type === 'row' &&
    !currentInspector.config?.payload?.trim()
  ) {
    return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.ROW_ID_REQUIRED');
  }

  if (
    currentInspector.type === 'locationRequest' &&
    !currentInspector.config?.body?.trim()
  ) {
    return t(
      'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LOCATION_REQUEST_BODY_REQUIRED'
    );
  }

  if (currentInspector.type === 'location') {
    const latitude = Number(currentInspector.config?.latitude);
    const longitude = Number(currentInspector.config?.longitude);

    if (
      currentInspector.config?.latitude === '' ||
      currentInspector.config?.latitude === null ||
      currentInspector.config?.longitude === '' ||
      currentInspector.config?.longitude === null
    ) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LOCATION_COORDINATES_REQUIRED'
      );
    }

    if (
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude) ||
      latitude < -90 ||
      latitude > 90 ||
      longitude < -180 ||
      longitude > 180
    ) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.LOCATION_COORDINATES_INVALID'
      );
    }
  }

  if (
    currentInspector.type === 'contact' &&
    (!currentInspector.config?.formattedName?.trim() ||
      !currentInspector.config?.phoneNumber?.trim())
  ) {
    return t(
      'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CONTACT_REQUIRED_FIELDS'
    );
  }

  if (currentInspector.type === 'template') {
    const currentTemplate = selectedTemplate(
      currentInspector.config?.templateId
    );
    if (!currentTemplate || !currentInspector.config?.templateName) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.TEMPLATE_SELECTION_REQUIRED'
      );
    }

    if (
      !templateParametersComplete(
        currentTemplate,
        currentInspector.config?.processedParams || {}
      )
    ) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.TEMPLATE_PARAMS_REQUIRED'
      );
    }
  }

  if (currentInspector.type === 'carousel') {
    const cards = Array.isArray(currentInspector.config?.cards)
      ? currentInspector.config.cards
      : [];

    if (!currentInspector.config?.body?.trim()) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_BODY_REQUIRED'
      );
    }

    if (cards.length < 2) {
      return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_CARD_MIN');
    }

    if (cards.length > 10) {
      return t('CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_CARD_MAX');
    }

    const missingMedia = cards.some(
      card => !card?.blobId && !card?.mediaLink?.trim()
    );
    if (missingMedia) {
      return t(
        'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_MEDIA_REQUIRED'
      );
    }

    if (currentInspector.config?.actionType === 'url') {
      const missingUrl = cards.some(
        card => !card?.buttonText?.trim() || !card?.buttonUrl?.trim()
      );
      if (missingUrl) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_URL_REQUIRED'
        );
      }
    }

    if (currentInspector.config?.actionType === 'quickReply') {
      const invalidReplies = cards.some(card => {
        const replies = Array.isArray(card?.quickReplies)
          ? card.quickReplies
          : [];
        return (
          !replies.length ||
          replies.length > 2 ||
          replies.some(reply => !reply?.title?.trim())
        );
      });

      if (invalidReplies) {
        return t(
          'CONVERSATION_FLOWS.EDITOR.PUBLISH_ERRORS.CAROUSEL_QUICK_REPLY_REQUIRED'
        );
      }
    }
  }

  return '';
};

const saveInspector = async () => {
  const editor = editorRef.value;
  const area = areaRef.value;
  const node = editor?.getNode(inspector.value?.id);
  if (!node || !area) return;

  const validationError = validateInspectorConfig(inspector.value);
  if (validationError) {
    useAlert(validationError);
    return;
  }

  if (inspector.value.type === 'button') {
    inspector.value.config.addLabelIds = normalizeMultiValue(
      inspector.value.config?.addLabelIds
    );
    inspector.value.config.removeLabelIds = normalizeMultiValue(
      inspector.value.config?.removeLabelIds
    );

    if (inspector.value.config?.nextStepType === 'flow') {
      inspector.value.config.title =
        selectedFlowName(inspector.value.config?.linkedFlowId) || '';
      inspector.value.config.payload = buildButtonPayload({
        nodeId: node.id,
        title: inspector.value.config?.title,
        existingPayload: node.data?.config?.payload,
      });
      const nextConnections = editor
        .getConnections()
        .filter(
          connection =>
            connection.source === node.id && connection.sourceOutput === 'next'
        );

      await nextConnections.reduce(
        (promise, connection) =>
          promise.then(() => editor.removeConnection(connection.id)),
        Promise.resolve()
      );
    } else {
      inspector.value.config.linkedFlowId = '';
      inspector.value.config.payload = buildButtonPayload({
        nodeId: node.id,
        title: inspector.value.config?.title,
        existingPayload: node.data?.config?.payload,
      });
    }
  }

  if (inspector.value.type === 'template') {
    const currentTemplate = selectedInspectorTemplate.value;

    if (!currentTemplate) {
      useAlert(t('CONVERSATION_FLOWS.EDITOR.TEMPLATE.SELECTION_REQUIRED'));
      return;
    }

    inspector.value.config = {
      ...defaultTemplateConfig(),
      ...inspector.value.config,
      templateId: currentTemplate.id,
      templateName: currentTemplate.name || '',
      namespace: currentTemplate.namespace || '',
      category: currentTemplate.category || '',
      language: currentTemplate.language || '',
      processedParams: JSON.parse(
        JSON.stringify(
          templateParserRef.value?.processedParams ||
            inspector.value.config?.processedParams ||
            {}
        )
      ),
      renderedBody:
        templateParserRef.value?.renderedTemplate ||
        inspector.value.config?.renderedBody ||
        '',
    };
  }

  if (inspector.value.type === 'contact') {
    inspector.value.config = normalizeContactConfig(inspector.value.config);
    inspector.value.config.formattedName =
      inspector.value.config.formattedName?.trim() || '';
    inspector.value.config.phoneNumber = normalizeContactPhone(
      inspector.value.config.phoneNumber
    );
    inspector.value.config.waId = deriveContactWaId(
      inspector.value.config.phoneNumber
    );
    inspector.value.config.firstName =
      inspector.value.config.formattedName.split(/\s+/).filter(Boolean)[0] ||
      '';
    inspector.value.config.phones = [
      {
        phone: inspector.value.config.phoneNumber,
        type: 'CELL',
        waId: inspector.value.config.waId,
      },
    ];
    inspector.value.config.emails = [];
    inspector.value.config.urls = [];
    inspector.value.config.addresses = [];
  }

  if (inspector.value.type === 'location') {
    inspector.value.config.latitude = normalizeLocationCoordinate(
      inspector.value.config.latitude
    );
    inspector.value.config.longitude = normalizeLocationCoordinate(
      inspector.value.config.longitude
    );
    inspector.value.config.name = inspector.value.config.name?.trim() || '';
    inspector.value.config.address =
      inspector.value.config.address?.trim() || '';
  }

  if (inspector.value.type === 'locationRequest') {
    inspector.value.config.body = inspector.value.config.body?.trim() || '';
  }

  if (inspector.value.type === 'carousel') {
    inspector.value.config = normalizeCarouselConfig(inspector.value.config);
    inspector.value.config.body = inspector.value.config.body?.trim() || '';

    inspector.value.config.cards = inspector.value.config.cards.map(card => ({
      ...normalizeCarouselCard(card),
      body: card?.body?.trim() || '',
      mediaLink: card?.mediaLink?.trim() || '',
      buttonText: card?.buttonText?.trim() || '',
      buttonUrl: card?.buttonUrl?.trim() || '',
      quickReplies: Array.isArray(card?.quickReplies)
        ? card.quickReplies
            .map(reply => ({
              title: reply?.title?.trim() || '',
            }))
            .filter(reply => reply.title)
        : [],
    }));

    const legacyButtonConnections = editor
      .getConnections()
      .filter(
        connection =>
          connection.source === node.id && connection.sourceOutput === 'buttons'
      );

    await legacyButtonConnections.reduce(
      (promise, connection) =>
        promise.then(() => editor.removeConnection(connection.id)),
      Promise.resolve()
    );
  }

  if (inspector.value.type === 'ai') {
    inspector.value.config.instructions =
      inspector.value.config.instructions?.trim() || '';
    inspector.value.config.fallbackMessage =
      inspector.value.config.fallbackMessage?.trim() || '';
  }

  if (inspector.value.type === 'noMatch') {
    inspector.value.config.title = t(
      'CONVERSATION_FLOWS.EDITOR.NO_MATCH.FIXED_TITLE'
    );
  }

  const entryNodeLabel =
    inspector.value.type === 'noMatch'
      ? nodeDefinitions.noMatch.label
      : nodeDefinitions.start.label;

  node.label = ['start', 'noMatch'].includes(inspector.value.type)
    ? inspector.value.config?.title || entryNodeLabel
    : inspector.value.label;
  if (node.controls?.message) {
    const messageValue = [
      'text',
      'interactive',
      'locationRequest',
      'carousel',
    ].includes(inspector.value.type)
      ? inspector.value.config?.body
      : inspector.value.message;
    node.controls.message.setValue(messageValue);
  }
  if (node.controls?.condition) {
    node.controls.condition.setValue(inspector.value.condition);
  }
  const summaryByType = {
    noMatch: inspector.value.config?.title,
    start: startNodeSummary(inspector.value),
    button: buttonNodeSummary(inspector.value),
    template: templateNodeSummary(inspector.value),
    ai: [
      inspector.value.config?.instructions,
      inspector.value.config?.fallbackMessage,
    ]
      .filter(Boolean)
      .join(' · '),
    carousel: defaultNodeSummary(inspector.value),
  };
  node.data.summary =
    summaryByType[inspector.value.type] ||
    defaultNodeSummary(inspector.value) ||
    node.data.description;
  node.data.config = inspector.value.config || {};

  await area.update('node', node.id);
  closeInspector();
};

const seedInitialFlow = async () => {
  const editor = editorRef.value;
  const area = areaRef.value;
  if (!editor || !area) return;

  const startNode = createNode({
    type: 'start',
    position: { x: 420, y: 180 },
  });

  await editor.addNode(startNode);
  await area.translate(startNode.id, { x: 420, y: 180 });
};

const loadDefinition = async definition => {
  const editor = editorRef.value;
  const area = areaRef.value;
  if (!editor || !area) return;

  const nodes = Array.isArray(definition?.nodes) ? definition.nodes : [];
  const connections =
    definition?.connections || definition?.edges || definition?.links || [];

  if (!nodes.length) {
    await seedInitialFlow();
    return;
  }

  const nodeMap = new Map();
  if (!nodes.some(node => ['start', 'noMatch'].includes(node.type))) {
    await addEditorNode(
      { type: 'start', position: { x: 420, y: 180 } },
      nodeMap
    );
  }
  await nodes.reduce(
    (promise, nodeDefinition) =>
      promise.then(() => addEditorNode(nodeDefinition, nodeMap)),
    Promise.resolve()
  );

  await connections.reduce(
    (promise, connectionDefinition) =>
      promise.then(() => addEditorConnection(connectionDefinition, nodeMap)),
    Promise.resolve()
  );
};

onMounted(async () => {
  const editor = new NodeEditor();
  const area = new AreaPlugin(canvasRef.value);
  const connection = new ConnectionPlugin();
  const render = new VuePlugin();

  render.addPreset(
    VuePresets.classic.setup({
      customize: {
        connection: () => FlowConnection,
        control: () => FlowControl,
        node: () => FlowNode,
        socket: () => FlowSocket,
      },
    })
  );
  connection.addPreset(
    () =>
      new ClassicFlow({
        canMakeConnection: canMakeFlowConnection,
        makeConnection: makeFlowConnection,
      })
  );

  editor.use(area);
  area.use(connection);
  area.use(render);

  AreaExtensions.selectableNodes(area, AreaExtensions.selector(), {
    accumulating: AreaExtensions.accumulateOnCtrl(),
  });
  AreaExtensions.simpleNodesOrder(area);

  editorRef.value = editor;
  areaRef.value = area;

  await loadDefinition(props.definition);
  await fitView();
  window.addEventListener('conversation-flow-delete-node', handleNodeDelete);
  window.addEventListener('conversation-flow-edit-node', handleNodeEdit);
  window.addEventListener(
    'conversation-flow-add-child-node',
    handleNodeAddChild
  );
});

onBeforeUnmount(() => {
  revokeInteractiveMediaPreviewUrl();
  window.removeEventListener('conversation-flow-delete-node', handleNodeDelete);
  window.removeEventListener('conversation-flow-edit-node', handleNodeEdit);
  window.removeEventListener(
    'conversation-flow-add-child-node',
    handleNodeAddChild
  );
  areaRef.value?.destroy();
});

defineExpose({
  addNode,
  fitView,
  getDefinition,
  getFlowName,
});
</script>

<template>
  <div dir="ltr" class="flex flex-col h-full min-h-[calc(100vh-12rem)]">
    <div
      class="flex items-center justify-between gap-3 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
    >
      <div class="flex items-center gap-2 overflow-x-auto">
        <button
          v-for="tool in nodeTools"
          :key="tool.type"
          v-tooltip.bottom="tool.label"
          type="button"
          draggable="true"
          class="inline-flex items-center justify-center flex-shrink-0 transition-colors border border-dashed rounded-full size-10 border-n-weak bg-n-solid-1 text-n-blue-10 hover:border-n-blue-8 hover:bg-n-blue-2 focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          :aria-label="tool.label"
          @click="addToolNode(tool.type)"
          @dragstart="handleToolDragStart($event, tool.type)"
        >
          <span :class="tool.icon" class="size-4" />
        </button>
      </div>
      <div class="flex items-center gap-2">
        <Button
          v-tooltip.bottom="$t('CONVERSATION_FLOWS.EDITOR.FIT')"
          icon="i-lucide-scan"
          slate
          sm
          @click="fitView"
        />
      </div>
    </div>
    <div class="relative flex-1">
      <div
        ref="canvasRef"
        class="relative h-full min-h-[calc(100vh-15rem)] overflow-hidden bg-n-background bg-[linear-gradient(to_right,rgb(var(--slate-4)/0.45)_1px,transparent_1px),linear-gradient(to_bottom,rgb(var(--slate-4)/0.45)_1px,transparent_1px)] bg-[size:44px_44px]"
        @dragover.prevent
        @drop.prevent="handleCanvasDrop"
      />
      <aside
        v-if="inspector"
        :dir="inspectorDirection"
        class="absolute top-4 left-4 z-20 w-[28rem] max-w-[calc(100%-2rem)] max-h-[calc(100%-2rem)] p-4 overflow-y-auto border rounded-lg shadow-lg bg-n-solid-1 border-n-weak"
      >
        <div class="flex items-center justify-between gap-3">
          <h3 class="m-0 text-sm font-semibold text-n-slate-12">
            {{ $t('CONVERSATION_FLOWS.EDITOR.NODE_SETTINGS') }}
          </h3>
          <button
            type="button"
            class="i-lucide-x size-4 text-n-slate-11"
            @click="closeInspector"
          />
        </div>
        <template v-if="inspector.type === 'noMatch'">
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.NO_MATCH.TITLE') }}
          </label>
          <input
            :value="$t('CONVERSATION_FLOWS.EDITOR.NO_MATCH.FIXED_TITLE')"
            type="text"
            disabled
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-2 border-n-weak text-n-slate-11 cursor-not-allowed"
          />
        </template>

        <template v-else-if="inspector.type === 'start'">
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.START.KEYWORDS') }}
          </label>
          <input
            v-model="inspector.config.triggerKeywords"
            type="text"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.START.KEYWORDS_HINT')"
          />

          <p class="mt-4 mb-2 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.START.MATCH_TYPE') }}
          </p>
          <div
            class="grid grid-cols-2 overflow-hidden border rounded-lg border-n-weak"
          >
            <button
              type="button"
              class="h-9 text-sm"
              :class="
                inspector.config.matchType === 'exact'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.matchType = 'exact'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.START.MATCH_EXACT') }}
            </button>
            <button
              type="button"
              class="h-9 text-sm border-l border-n-weak"
              :class="
                inspector.config.matchType === 'contains'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.matchType = 'contains'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.START.MATCH_CONTAINS') }}
            </button>
          </div>

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.START.TITLE') }}
          </label>
          <input
            v-model="inspector.config.title"
            type="text"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.START.TITLE_HINT')"
          />

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.START.ASSIGN_AGENT') }}
          </label>
          <select
            v-model="inspector.config.assignedAgentId"
            class="w-full h-9 px-2 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
          >
            <option value="">
              {{ $t('CONVERSATION_FLOWS.EDITOR.START.NONE') }}
            </option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.START.WEBHOOK_URL') }}
          </label>
          <input
            v-model="inspector.config.webhookUrl"
            type="url"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.START.WEBHOOK_HINT')"
          />
          <p
            v-if="!isValidWebhookUrl(inspector.config.webhookUrl)"
            class="mt-1 mb-0 text-xs text-n-ruby-9"
          >
            {{ $t('CONVERSATION_FLOWS.EDITOR.START.WEBHOOK_ERROR') }}
          </p>
        </template>

        <template v-else-if="inspector.type === 'text'">
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.TEXT.HEADER') }}
          </label>
          <input
            v-model="inspector.config.header"
            type="text"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.TEXT.HEADER_HINT')"
          />
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.TEXT.BODY') }}
          </label>
          <textarea
            v-model="inspector.config.body"
            class="w-full h-32 px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.TEXT.BODY_HINT')"
          />
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.TEXT.FOOTER') }}
          </label>
          <input
            v-model="inspector.config.footer"
            type="text"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.TEXT.FOOTER_HINT')"
          />
        </template>

        <template v-else-if="inspector.type === 'image'">
          <p class="mt-4 mb-2 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.IMAGE.SOURCE') }}
          </p>
          <div
            class="grid grid-cols-2 overflow-hidden border rounded-lg border-n-weak"
          >
            <button
              type="button"
              class="h-9 px-3 text-sm"
              :class="
                inspector.config.mediaSource === 'uploaded'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.mediaSource = 'uploaded'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.IMAGE.UPLOAD') }}
            </button>
            <button
              type="button"
              class="h-9 px-3 text-sm border-l border-n-weak"
              :class="
                inspector.config.mediaSource === 'link'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.mediaSource = 'link'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.IMAGE.LINK') }}
            </button>
          </div>

          <template v-if="inspector.config.mediaSource === 'uploaded'">
            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.IMAGE.UPLOAD_LABEL') }}
            </label>
            <label
              class="flex items-center justify-center w-full min-h-28 overflow-hidden border border-dashed rounded-lg cursor-pointer border-n-blue-9 bg-n-alpha-1 text-n-slate-11"
            >
              <input
                id="conversation-flow-image-upload"
                type="file"
                accept="image/*"
                class="hidden"
                @change="uploadImageMedia"
              />
              <img
                v-if="interactiveMediaPreviewUrl"
                :src="interactiveMediaPreviewUrl"
                :alt="inspector.config.fileName"
                class="object-contain w-full max-h-44"
              />
              <span v-else class="i-lucide-cloud-upload size-5" />
            </label>
            <p class="mt-2 mb-0 text-xs text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.IMAGE.SIZE_HINT') }}
            </p>
            <p
              v-if="inspector.config.fileName"
              class="mt-1 mb-0 text-xs text-n-slate-12"
            >
              {{ inspector.config.fileName }}
            </p>
            <div
              v-if="inspector.config.fileName || interactiveMediaPreviewUrl"
              class="flex items-center gap-2 mt-3"
            >
              <button
                type="button"
                class="inline-flex items-center h-8 px-3 text-xs font-medium rounded-lg bg-n-blue-2 text-n-blue-11"
                @click="replaceInspectorNodeMedia('image')"
              >
                {{ $t('CONVERSATION_FLOWS.EDITOR.IMAGE.REPLACE') }}
              </button>
              <button
                type="button"
                class="inline-flex items-center h-8 px-3 text-xs font-medium rounded-lg bg-n-ruby-2 text-n-ruby-11"
                @click="resetInspectorNodeMedia('image')"
              >
                {{ $t('CONVERSATION_FLOWS.EDITOR.IMAGE.REMOVE') }}
              </button>
            </div>
          </template>

          <template v-else>
            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.IMAGE.LINK_LABEL') }}
            </label>
            <input
              v-model="inspector.config.mediaLink"
              type="url"
              class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
              :placeholder="$t('CONVERSATION_FLOWS.EDITOR.IMAGE.LINK_HINT')"
            />
          </template>

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.IMAGE.CAPTION') }}
          </label>
          <textarea
            v-model="inspector.config.body"
            maxlength="1024"
            class="w-full h-28 px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.IMAGE.CAPTION_HINT')"
          />
        </template>

        <template v-else-if="inspector.type === 'video'">
          <p class="mt-4 mb-2 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.VIDEO.SOURCE') }}
          </p>
          <div
            class="grid grid-cols-2 overflow-hidden border rounded-lg border-n-weak"
          >
            <button
              type="button"
              class="h-9 px-3 text-sm"
              :class="
                inspector.config.mediaSource === 'uploaded'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.mediaSource = 'uploaded'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.VIDEO.UPLOAD') }}
            </button>
            <button
              type="button"
              class="h-9 px-3 text-sm border-l border-n-weak"
              :class="
                inspector.config.mediaSource === 'link'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.mediaSource = 'link'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.VIDEO.LINK') }}
            </button>
          </div>

          <template v-if="inspector.config.mediaSource === 'uploaded'">
            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.VIDEO.UPLOAD_LABEL') }}
            </label>
            <label
              class="flex items-center justify-center w-full min-h-28 overflow-hidden border border-dashed rounded-lg cursor-pointer border-n-blue-9 bg-n-alpha-1 text-n-slate-11"
            >
              <input
                id="conversation-flow-video-upload"
                type="file"
                accept="video/*"
                class="hidden"
                @change="uploadVideoMedia"
              />
              <video
                v-if="interactiveMediaPreviewUrl"
                :src="interactiveMediaPreviewUrl"
                class="object-contain w-full max-h-44"
                controls
              />
              <span v-else class="i-lucide-cloud-upload size-5" />
            </label>
            <p class="mt-2 mb-0 text-xs text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.VIDEO.SIZE_HINT') }}
            </p>
            <p
              v-if="inspector.config.fileName"
              class="mt-1 mb-0 text-xs text-n-slate-12"
            >
              {{ inspector.config.fileName }}
            </p>
            <div
              v-if="inspector.config.fileName || interactiveMediaPreviewUrl"
              class="flex items-center gap-2 mt-3"
            >
              <button
                type="button"
                class="inline-flex items-center h-8 px-3 text-xs font-medium rounded-lg bg-n-blue-2 text-n-blue-11"
                @click="replaceInspectorNodeMedia('video')"
              >
                {{ $t('CONVERSATION_FLOWS.EDITOR.VIDEO.REPLACE') }}
              </button>
              <button
                type="button"
                class="inline-flex items-center h-8 px-3 text-xs font-medium rounded-lg bg-n-ruby-2 text-n-ruby-11"
                @click="resetInspectorNodeMedia('video')"
              >
                {{ $t('CONVERSATION_FLOWS.EDITOR.VIDEO.REMOVE') }}
              </button>
            </div>
          </template>

          <template v-else>
            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.VIDEO.LINK_LABEL') }}
            </label>
            <input
              v-model="inspector.config.mediaLink"
              type="url"
              class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
              :placeholder="$t('CONVERSATION_FLOWS.EDITOR.VIDEO.LINK_HINT')"
            />
          </template>

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.VIDEO.CAPTION') }}
          </label>
          <textarea
            v-model="inspector.config.body"
            maxlength="1024"
            class="w-full h-28 px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.VIDEO.CAPTION_HINT')"
          />
        </template>

        <template v-else-if="inspector.type === 'audio'">
          <p class="mt-4 mb-2 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.AUDIO.SOURCE') }}
          </p>
          <div
            class="grid grid-cols-2 overflow-hidden border rounded-lg border-n-weak"
          >
            <button
              type="button"
              class="h-9 px-3 text-sm"
              :class="
                inspector.config.mediaSource === 'uploaded'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.mediaSource = 'uploaded'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.AUDIO.UPLOAD') }}
            </button>
            <button
              type="button"
              class="h-9 px-3 text-sm border-l border-n-weak"
              :class="
                inspector.config.mediaSource === 'link'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.mediaSource = 'link'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.AUDIO.LINK') }}
            </button>
          </div>

          <template v-if="inspector.config.mediaSource === 'uploaded'">
            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.AUDIO.UPLOAD_LABEL') }}
            </label>
            <label
              class="flex items-center justify-center w-full min-h-28 overflow-hidden border border-dashed rounded-lg cursor-pointer border-n-blue-9 bg-n-alpha-1 text-n-slate-11"
            >
              <input
                id="conversation-flow-audio-upload"
                type="file"
                accept="audio/*"
                class="hidden"
                @change="uploadAudioMedia"
              />
              <audio
                v-if="interactiveMediaPreviewUrl"
                :src="interactiveMediaPreviewUrl"
                class="w-full"
                controls
              />
              <span v-else class="i-lucide-cloud-upload size-5" />
            </label>
            <p class="mt-2 mb-0 text-xs text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.AUDIO.SIZE_HINT') }}
            </p>
            <p
              v-if="inspector.config.fileName"
              class="mt-1 mb-0 text-xs text-n-slate-12"
            >
              {{ inspector.config.fileName }}
            </p>
            <div
              v-if="inspector.config.fileName || interactiveMediaPreviewUrl"
              class="flex items-center gap-2 mt-3"
            >
              <button
                type="button"
                class="inline-flex items-center h-8 px-3 text-xs font-medium rounded-lg bg-n-blue-2 text-n-blue-11"
                @click="replaceInspectorNodeMedia('audio')"
              >
                {{ $t('CONVERSATION_FLOWS.EDITOR.AUDIO.REPLACE') }}
              </button>
              <button
                type="button"
                class="inline-flex items-center h-8 px-3 text-xs font-medium rounded-lg bg-n-ruby-2 text-n-ruby-11"
                @click="resetInspectorNodeMedia('audio')"
              >
                {{ $t('CONVERSATION_FLOWS.EDITOR.AUDIO.REMOVE') }}
              </button>
            </div>
          </template>

          <template v-else>
            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.AUDIO.LINK_LABEL') }}
            </label>
            <input
              v-model="inspector.config.mediaLink"
              type="url"
              class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
              :placeholder="$t('CONVERSATION_FLOWS.EDITOR.AUDIO.LINK_HINT')"
            />
          </template>
        </template>

        <template v-else-if="inspector.type === 'file'">
          <p class="mt-4 mb-2 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.FILE.SOURCE') }}
          </p>
          <div
            class="grid grid-cols-2 overflow-hidden border rounded-lg border-n-weak"
          >
            <button
              type="button"
              class="h-9 px-3 text-sm"
              :class="
                inspector.config.mediaSource === 'uploaded'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.mediaSource = 'uploaded'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.FILE.UPLOAD') }}
            </button>
            <button
              type="button"
              class="h-9 px-3 text-sm border-l border-n-weak"
              :class="
                inspector.config.mediaSource === 'link'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.mediaSource = 'link'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.FILE.LINK') }}
            </button>
          </div>

          <template v-if="inspector.config.mediaSource === 'uploaded'">
            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.FILE.UPLOAD_LABEL') }}
            </label>
            <label
              class="flex items-center justify-center w-full min-h-28 overflow-hidden border border-dashed rounded-lg cursor-pointer border-n-blue-9 bg-n-alpha-1 text-n-slate-11"
            >
              <input
                id="conversation-flow-file-upload"
                type="file"
                class="hidden"
                @change="uploadFileMedia"
              />
              <div
                v-if="inspector.config.fileName"
                class="flex flex-col items-center justify-center gap-2"
              >
                <span class="i-lucide-file-text size-8 text-n-blue-11" />
                <span
                  class="px-4 text-sm font-medium text-center text-n-slate-12 break-all"
                >
                  {{ inspector.config.fileName }}
                </span>
              </div>
              <span v-else class="i-lucide-cloud-upload size-5" />
            </label>
            <p class="mt-2 mb-0 text-xs text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.FILE.SIZE_HINT') }}
            </p>
            <p
              v-if="inspector.config.fileName"
              class="mt-1 mb-0 text-xs text-n-slate-12"
            >
              {{ inspector.config.fileName }}
            </p>
            <div
              v-if="inspector.config.fileName"
              class="flex items-center gap-2 mt-3"
            >
              <button
                type="button"
                class="inline-flex items-center h-8 px-3 text-xs font-medium rounded-lg bg-n-blue-2 text-n-blue-11"
                @click="replaceInspectorNodeMedia('file')"
              >
                {{ $t('CONVERSATION_FLOWS.EDITOR.FILE.REPLACE') }}
              </button>
              <button
                type="button"
                class="inline-flex items-center h-8 px-3 text-xs font-medium rounded-lg bg-n-ruby-2 text-n-ruby-11"
                @click="resetInspectorNodeMedia('file')"
              >
                {{ $t('CONVERSATION_FLOWS.EDITOR.FILE.REMOVE') }}
              </button>
            </div>
          </template>

          <template v-else>
            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.FILE.LINK_LABEL') }}
            </label>
            <input
              v-model="inspector.config.mediaLink"
              type="url"
              class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
              :placeholder="$t('CONVERSATION_FLOWS.EDITOR.FILE.LINK_HINT')"
            />
          </template>

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.FILE.CAPTION') }}
          </label>
          <textarea
            v-model="inspector.config.body"
            maxlength="1024"
            class="w-full h-28 px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.FILE.CAPTION_HINT')"
          />
        </template>

        <template v-else-if="inspector.type === 'interactive'">
          <label class="block mt-4 mb-2 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.HEADER') }}
          </label>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="inline-flex items-center justify-center w-9 h-9 border border-dashed rounded-full border-n-weak"
              :class="
                inspector.config.header.type === 'text'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-solid-1 text-n-blue-9'
              "
              @click="
                inspector.config.header.enabled = true;
                inspector.config.header.type = 'text';
              "
            >
              <span class="i-lucide-table-2 size-4" />
            </button>
            <button
              type="button"
              class="inline-flex items-center justify-center w-9 h-9 border border-dashed rounded-full border-n-weak"
              :class="
                inspector.config.header.type === 'media'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-solid-1 text-n-blue-9'
              "
              @click="
                inspector.config.header.enabled = true;
                inspector.config.header.type = 'media';
              "
            >
              <span class="i-lucide-upload size-4" />
            </button>
          </div>

          <template
            v-if="
              inspector.config.header.enabled &&
              inspector.config.header.type === 'text'
            "
          >
            <input
              v-model="inspector.config.header.text"
              type="text"
              maxlength="60"
              class="w-full h-9 px-3 mt-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
              :placeholder="
                $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.HEADER_TEXT_HINT')
              "
            />
          </template>

          <template
            v-if="
              inspector.config.header.enabled &&
              inspector.config.header.type === 'media'
            "
          >
            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.MEDIA_UPLOAD') }}
            </label>
            <label
              class="flex items-center justify-center w-full min-h-28 overflow-hidden border border-dashed rounded-lg cursor-pointer border-n-blue-9 bg-n-alpha-1 text-n-slate-11"
            >
              <input
                type="file"
                accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                class="hidden"
                @change="uploadInteractiveHeaderMedia"
              />
              <img
                v-if="
                  interactiveMediaPreviewUrl &&
                  inspector.config.header.mediaType === 'image'
                "
                :src="interactiveMediaPreviewUrl"
                :alt="inspector.config.header.fileName"
                class="object-contain w-full max-h-44"
              />
              <video
                v-else-if="
                  interactiveMediaPreviewUrl &&
                  inspector.config.header.mediaType === 'video'
                "
                :src="interactiveMediaPreviewUrl"
                class="object-contain w-full max-h-44"
                controls
              />
              <span v-else class="i-lucide-cloud-upload size-5" />
            </label>
            <p class="mt-2 mb-0 text-xs text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.MEDIA_LIMITS') }}
            </p>
            <p
              v-if="inspector.config.header.fileName"
              class="mt-1 mb-0 text-xs text-n-slate-12"
            >
              {{ inspector.config.header.fileName }}
            </p>
          </template>

          <label class="block mt-6 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.BODY') }}
          </label>
          <textarea
            v-model="inspector.config.body"
            maxlength="1024"
            class="w-full h-36 px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.BODY_HINT')"
          />

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.FOOTER') }}
          </label>
          <input
            v-model="inspector.config.footer"
            type="text"
            maxlength="60"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="
              $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.FOOTER_HINT')
            "
          />

          <div class="flex items-center justify-between gap-3 mt-4">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.TYPING') }}
            </span>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                v-model="inspector.config.typingOnDisplay"
                type="checkbox"
                class="sr-only peer"
              />
              <span
                class="w-9 h-5 rounded-full bg-n-slate-5 peer-checked:bg-n-blue-9"
              />
              <span
                class="absolute w-4 h-4 transition bg-white rounded-full start-0.5 peer-checked:translate-x-4"
              />
            </label>
          </div>

          <div class="mt-5">
            <p class="mb-3 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.SMART_DELAY') }}
            </p>
            <label class="block text-xs text-n-slate-12">
              {{
                $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.SECONDS', {
                  value: inspector.config.delay.seconds,
                })
              }}
            </label>
            <input
              v-model.number="inspector.config.delay.seconds"
              type="range"
              min="0"
              max="59"
              class="w-full"
            />
            <label class="block mt-3 text-xs text-n-slate-12">
              {{
                $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.MINUTES', {
                  value: inspector.config.delay.minutes,
                })
              }}
            </label>
            <input
              v-model.number="inspector.config.delay.minutes"
              type="range"
              min="0"
              max="59"
              class="w-full"
            />
            <label class="block mt-3 text-xs text-n-slate-12">
              {{
                $t('CONVERSATION_FLOWS.EDITOR.INTERACTIVE.HOURS', {
                  value: inspector.config.delay.hours,
                })
              }}
            </label>
            <input
              v-model.number="inspector.config.delay.hours"
              type="range"
              min="0"
              max="23"
              class="w-full"
            />
          </div>
        </template>

        <template v-else-if="inspector.type === 'button'">
          <p class="mt-4 mb-2 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.BUTTON.NEXT_STEP') }}
          </p>
          <div
            class="grid grid-cols-2 overflow-hidden border rounded-lg border-n-weak"
          >
            <button
              type="button"
              class="h-9 px-3 text-sm"
              :class="
                inspector.config.nextStepType === 'message'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="updateButtonInspectorMode('message')"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.BUTTON.NEXT_MESSAGE') }}
            </button>
            <button
              type="button"
              class="h-9 px-3 text-sm border-l border-n-weak"
              :class="
                inspector.config.nextStepType === 'flow'
                  ? 'bg-n-blue-9 text-white'
                  : 'bg-n-alpha-1 text-n-slate-12'
              "
              @click="updateButtonInspectorMode('flow')"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.BUTTON.NEXT_FLOW') }}
            </button>
          </div>

          <template v-if="inspector.config.nextStepType === 'flow'">
            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.BUTTON.LINKED_FLOW') }}
            </label>
            <select
              v-model="inspector.config.linkedFlowId"
              class="w-full h-9 px-2 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            >
              <option value="">
                {{ $t('CONVERSATION_FLOWS.EDITOR.START.NONE') }}
              </option>
              <option
                v-for="flow in publishedFlows"
                :key="flow.id"
                :value="flow.id"
              >
                {{ flow.name }}
              </option>
            </select>
          </template>

          <template v-else>
            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.BUTTON.TITLE') }}
            </label>
            <input
              v-model="inspector.config.title"
              type="text"
              maxlength="20"
              class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
              :placeholder="$t('CONVERSATION_FLOWS.EDITOR.BUTTON.TITLE_HINT')"
            />
            <p class="mt-1 mb-0 text-xs text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.BUTTON.TITLE_LIMIT') }}
            </p>

            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.START.ADD_LABELS') }}
            </label>
            <div class="flex items-center gap-2">
              <select
                v-model="inspector.addLabelDraft"
                class="flex-1 h-9 px-2 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
                @change="addButtonLabel('add')"
              >
                <option value="">
                  {{ $t('CONVERSATION_FLOWS.EDITOR.BUTTON.SELECT_LABEL') }}
                </option>
                <option
                  v-for="label in availableLabelsFor(
                    inspector.config.addLabelIds
                  )"
                  :key="`add-${label.id}`"
                  :value="label.id"
                >
                  {{ label.title }}
                </option>
              </select>
            </div>
            <div
              v-if="selectedLabelRecords(inspector.config.addLabelIds).length"
              class="flex flex-wrap gap-2 min-h-14 mt-2 px-3 py-2 border rounded-lg bg-n-alpha-1 border-n-weak"
            >
              <button
                v-for="label in selectedLabelRecords(
                  inspector.config.addLabelIds
                )"
                :key="`add-chip-${label.id}`"
                type="button"
                class="inline-flex items-center gap-2 px-2 py-1 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
                @click="removeButtonLabel('add', label.id)"
              >
                <span>{{ label.title }}</span>
                <span class="i-lucide-x size-3" />
              </button>
            </div>

            <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.START.REMOVE_LABELS') }}
            </label>
            <div class="flex items-center gap-2">
              <select
                v-model="inspector.removeLabelDraft"
                class="flex-1 h-9 px-2 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
                @change="addButtonLabel('remove')"
              >
                <option value="">
                  {{ $t('CONVERSATION_FLOWS.EDITOR.BUTTON.SELECT_LABEL') }}
                </option>
                <option
                  v-for="label in availableLabelsFor(
                    inspector.config.removeLabelIds
                  )"
                  :key="`remove-${label.id}`"
                  :value="label.id"
                >
                  {{ label.title }}
                </option>
              </select>
            </div>
            <div
              v-if="
                selectedLabelRecords(inspector.config.removeLabelIds).length
              "
              class="flex flex-wrap gap-2 min-h-14 mt-2 px-3 py-2 border rounded-lg bg-n-alpha-1 border-n-weak"
            >
              <button
                v-for="label in selectedLabelRecords(
                  inspector.config.removeLabelIds
                )"
                :key="`remove-chip-${label.id}`"
                type="button"
                class="inline-flex items-center gap-2 px-2 py-1 text-xs border rounded-md border-n-weak bg-n-solid-1 text-n-slate-12"
                @click="removeButtonLabel('remove', label.id)"
              >
                <span>{{ label.title }}</span>
                <span class="i-lucide-x size-3" />
              </button>
            </div>
          </template>
        </template>

        <template v-else-if="inspector.type === 'keyboard'">
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.LIST_MESSAGE.BUTTON_TEXT') }}
          </label>
          <input
            v-model="inspector.config.buttonText"
            type="text"
            maxlength="20"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="
              $t('CONVERSATION_FLOWS.EDITOR.LIST_MESSAGE.BUTTON_TEXT_HINT')
            "
          />
        </template>

        <template v-else-if="inspector.type === 'section'">
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.SECTION.TITLE') }}
          </label>
          <input
            v-model="inspector.config.title"
            type="text"
            maxlength="24"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.SECTION.TITLE_HINT')"
          />
        </template>

        <template v-else-if="['row', 'listMessage'].includes(inspector.type)">
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.LIST_MESSAGE.TITLE') }}
          </label>
          <input
            v-model="inspector.config.title"
            type="text"
            maxlength="24"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="
              $t('CONVERSATION_FLOWS.EDITOR.LIST_MESSAGE.TITLE_HINT')
            "
          />

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.ROW.PAYLOAD') }}
          </label>
          <input
            v-model="inspector.config.payload"
            type="text"
            maxlength="200"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.ROW.PAYLOAD_HINT')"
          />

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.LIST_MESSAGE.DESCRIPTION') }}
          </label>
          <input
            v-model="inspector.config.description"
            type="text"
            maxlength="72"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="
              $t('CONVERSATION_FLOWS.EDITOR.LIST_MESSAGE.DESCRIPTION_HINT')
            "
          />
        </template>

        <template v-else-if="inspector.type === 'template'">
          <p class="mt-4 mb-2 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.TEMPLATE.SELECT') }}
          </p>
          <TemplatesPicker
            v-if="inboxId"
            :inbox-id="inboxId"
            @on-select="handleTemplateSelection"
          />
          <p v-else class="mt-2 mb-0 text-xs text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.TEMPLATE.INBOX_REQUIRED') }}
          </p>

          <div
            v-if="selectedInspectorTemplate"
            class="pt-4 mt-4 border-t border-n-weak"
          >
            <p class="mb-2 text-xs font-medium text-n-slate-11">
              {{ $t('CONVERSATION_FLOWS.EDITOR.TEMPLATE.CONFIGURATION') }}
            </p>
            <WhatsAppTemplateParser
              ref="templateParserRef"
              :template="selectedInspectorTemplate"
              :initial-processed-params="inspector.config.processedParams || {}"
            />
          </div>
        </template>

        <template v-else-if="inspector.type === 'location'">
          <div class="grid grid-cols-2 gap-3 mt-4">
            <div>
              <label class="block mb-1 text-xs font-medium text-n-slate-11">
                {{ $t('CONVERSATION_FLOWS.EDITOR.LOCATION.LATITUDE') }}
              </label>
              <input
                v-model="inspector.config.latitude"
                type="number"
                step="any"
                class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
                :placeholder="
                  $t('CONVERSATION_FLOWS.EDITOR.LOCATION.LATITUDE_HINT')
                "
              />
            </div>
            <div>
              <label class="block mb-1 text-xs font-medium text-n-slate-11">
                {{ $t('CONVERSATION_FLOWS.EDITOR.LOCATION.LONGITUDE') }}
              </label>
              <input
                v-model="inspector.config.longitude"
                type="number"
                step="any"
                class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
                :placeholder="
                  $t('CONVERSATION_FLOWS.EDITOR.LOCATION.LONGITUDE_HINT')
                "
              />
            </div>
          </div>

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.LOCATION.NAME') }}
          </label>
          <input
            v-model="inspector.config.name"
            type="text"
            maxlength="100"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.LOCATION.NAME_HINT')"
          />

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.LOCATION.ADDRESS') }}
          </label>
          <input
            v-model="inspector.config.address"
            type="text"
            maxlength="256"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.LOCATION.ADDRESS_HINT')"
          />
        </template>

        <template v-else-if="inspector.type === 'locationRequest'">
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.LOCATION_REQUEST.BODY') }}
          </label>
          <textarea
            v-model="inspector.config.body"
            maxlength="1024"
            class="w-full h-32 px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="
              $t('CONVERSATION_FLOWS.EDITOR.LOCATION_REQUEST.BODY_HINT')
            "
          />
          <p class="mt-2 mb-0 text-xs text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.LOCATION_REQUEST.HELP') }}
          </p>
        </template>

        <template v-else-if="inspector.type === 'carousel'">
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.BODY') }}
          </label>
          <textarea
            v-model="inspector.config.body"
            maxlength="1024"
            class="w-full h-24 px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.BODY_HINT')"
          />

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.ACTION_TYPE') }}
          </label>
          <div class="grid grid-cols-2 gap-2">
            <button
              type="button"
              class="h-10 px-3 text-sm border rounded-lg"
              :class="
                inspector.config.actionType === 'url'
                  ? 'border-n-blue-9 bg-n-brand text-white'
                  : 'border-n-weak bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.actionType = 'url'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.URL_MODE') }}
            </button>
            <button
              type="button"
              class="h-10 px-3 text-sm border rounded-lg"
              :class="
                inspector.config.actionType === 'quickReply'
                  ? 'border-n-blue-9 bg-n-brand text-white'
                  : 'border-n-weak bg-n-alpha-1 text-n-slate-12'
              "
              @click="inspector.config.actionType = 'quickReply'"
            >
              {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.QUICK_REPLY_MODE') }}
            </button>
          </div>

          <p class="mt-2 mb-0 text-xs text-n-slate-11">
            {{
              inspector.config.actionType === 'quickReply'
                ? $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.QUICK_REPLY_HELP')
                : $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.URL_HELP')
            }}
          </p>

          <div class="mt-4 space-y-4">
            <div
              v-for="(card, cardIndex) in inspector.config.cards"
              :key="cardIndex"
              class="p-4 border rounded-lg border-n-weak bg-n-alpha-1"
            >
              <div class="flex items-center justify-between gap-2">
                <p class="m-0 text-sm font-medium text-n-slate-12">
                  {{
                    $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.CARD_LABEL', {
                      index: cardIndex + 1,
                    })
                  }}
                </p>
                <Button
                  v-if="inspector.config.cards.length > 2"
                  icon="i-lucide-trash-2"
                  ghost
                  ruby
                  sm
                  @click="removeCarouselCard(cardIndex)"
                />
              </div>

              <label
                class="block mt-4 mb-1 text-xs font-medium text-n-slate-11"
              >
                {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.MEDIA_SOURCE') }}
              </label>
              <div class="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  class="h-10 px-3 text-sm border rounded-lg"
                  :class="
                    card.mediaSource === 'uploaded'
                      ? 'border-n-blue-9 bg-n-brand text-white'
                      : 'border-n-weak bg-n-alpha-1 text-n-slate-12'
                  "
                  @click="card.mediaSource = 'uploaded'"
                >
                  {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.UPLOAD') }}
                </button>
                <button
                  type="button"
                  class="h-10 px-3 text-sm border rounded-lg"
                  :class="
                    card.mediaSource === 'link'
                      ? 'border-n-blue-9 bg-n-brand text-white'
                      : 'border-n-weak bg-n-alpha-1 text-n-slate-12'
                  "
                  @click="card.mediaSource = 'link'"
                >
                  {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.LINK') }}
                </button>
              </div>

              <template v-if="card.mediaSource === 'uploaded'">
                <label
                  class="block mt-4 mb-1 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.UPLOAD_LABEL') }}
                </label>
                <input
                  :id="`conversation-flow-carousel-upload-${cardIndex}`"
                  type="file"
                  accept="image/*,video/*"
                  class="hidden"
                  @change="event => uploadCarouselCardMedia(event, cardIndex)"
                />
                <div class="flex flex-wrap gap-2">
                  <Button
                    icon="i-lucide-upload"
                    sm
                    :label="
                      card.fileName
                        ? $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.REPLACE')
                        : $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.UPLOAD')
                    "
                    @click="replaceCarouselCardMedia(cardIndex)"
                  />
                  <Button
                    v-if="card.fileName || card.mediaUrl"
                    icon="i-lucide-x"
                    ghost
                    ruby
                    sm
                    :label="$t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.REMOVE')"
                    @click="clearCarouselCardMedia(cardIndex)"
                  />
                </div>
                <p class="mt-2 mb-0 text-xs text-n-slate-11">
                  {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.MEDIA_SIZE_HINT') }}
                </p>
              </template>

              <template v-else>
                <label
                  class="block mt-4 mb-1 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.MEDIA_TYPE') }}
                </label>
                <select
                  v-model="card.mediaType"
                  class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
                >
                  <option value="image">
                    {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.MEDIA_IMAGE') }}
                  </option>
                  <option value="video">
                    {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.MEDIA_VIDEO') }}
                  </option>
                </select>

                <label
                  class="block mt-4 mb-1 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.LINK_LABEL') }}
                </label>
                <input
                  v-model="card.mediaLink"
                  type="url"
                  class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
                  :placeholder="
                    $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.LINK_HINT')
                  "
                />
              </template>

              <div
                v-if="card.mediaUrl || card.mediaLink"
                class="p-2 mt-4 overflow-hidden rounded-lg bg-n-slate-2"
              >
                <img
                  v-if="card.mediaType !== 'video'"
                  :src="card.mediaUrl || card.mediaLink"
                  class="object-contain w-full max-h-40 rounded-lg"
                />
                <video
                  v-else
                  :src="card.mediaUrl || card.mediaLink"
                  controls
                  class="object-contain w-full max-h-40 rounded-lg"
                />
              </div>

              <label
                class="block mt-4 mb-1 text-xs font-medium text-n-slate-11"
              >
                {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.CARD_BODY') }}
              </label>
              <textarea
                v-model="card.body"
                maxlength="160"
                class="w-full h-24 px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
                :placeholder="
                  $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.CARD_BODY_HINT')
                "
              />

              <template v-if="inspector.config.actionType === 'url'">
                <label
                  class="block mt-4 mb-1 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.BUTTON_TEXT') }}
                </label>
                <input
                  v-model="card.buttonText"
                  type="text"
                  maxlength="20"
                  class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
                  :placeholder="
                    $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.BUTTON_TEXT_HINT')
                  "
                />

                <label
                  class="block mt-4 mb-1 text-xs font-medium text-n-slate-11"
                >
                  {{ $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.BUTTON_URL') }}
                </label>
                <input
                  v-model="card.buttonUrl"
                  type="url"
                  class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
                  :placeholder="
                    $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.BUTTON_URL_HINT')
                  "
                />
              </template>
              <template v-else>
                <div class="mt-4 space-y-3">
                  <div
                    v-for="(reply, replyIndex) in card.quickReplies"
                    :key="`${cardIndex}-${replyIndex}`"
                    class="flex items-center gap-2"
                  >
                    <input
                      v-model="reply.title"
                      type="text"
                      maxlength="20"
                      class="flex-1 h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
                      :placeholder="
                        $t(
                          'CONVERSATION_FLOWS.EDITOR.CAROUSEL.BUTTON_TEXT_HINT'
                        )
                      "
                    />
                    <Button
                      icon="i-lucide-trash-2"
                      ghost
                      ruby
                      sm
                      @click="removeCarouselQuickReply(cardIndex, replyIndex)"
                    />
                  </div>

                  <Button
                    icon="i-lucide-plus"
                    faded
                    slate
                    sm
                    :label="
                      $t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.ADD_QUICK_REPLY')
                    "
                    :disabled="(card.quickReplies || []).length >= 2"
                    @click="addCarouselQuickReply(cardIndex)"
                  />
                </div>
              </template>
            </div>
          </div>

          <Button
            class="mt-4"
            icon="i-lucide-plus"
            faded
            slate
            sm
            :label="$t('CONVERSATION_FLOWS.EDITOR.CAROUSEL.ADD_CARD')"
            :disabled="inspector.config.cards.length >= 10"
            @click="addCarouselCard"
          />
        </template>

        <template v-else-if="inspector.type === 'ai'">
          <p class="mt-4 mb-0 text-xs text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.AI.HELP') }}
          </p>

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.AI.INSTRUCTIONS') }}
          </label>
          <textarea
            v-model="inspector.config.instructions"
            maxlength="4000"
            class="w-full h-32 px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.AI.INSTRUCTIONS_HINT')"
          />

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.AI.FALLBACK') }}
          </label>
          <textarea
            v-model="inspector.config.fallbackMessage"
            maxlength="1024"
            class="w-full h-24 px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.AI.FALLBACK_HINT')"
          />
        </template>

        <template v-else-if="inspector.type === 'contact'">
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.CONTACT.FORMATTED_NAME') }}
          </label>
          <input
            v-model="inspector.config.formattedName"
            type="text"
            maxlength="100"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="
              $t('CONVERSATION_FLOWS.EDITOR.CONTACT.FORMATTED_NAME_HINT')
            "
          />

          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.CONTACT.PHONE') }}
          </label>
          <input
            v-model="inspector.config.phoneNumber"
            type="text"
            maxlength="30"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
            :placeholder="$t('CONVERSATION_FLOWS.EDITOR.CONTACT.PHONE_HINT')"
          />
        </template>

        <template v-else>
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.NODE_NAME') }}
          </label>
          <input
            v-model="inspector.label"
            type="text"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
          />
          <label class="block mt-4 mb-1 text-xs font-medium text-n-slate-11">
            {{ $t('CONVERSATION_FLOWS.EDITOR.NODE_CONTENT') }}
          </label>
          <textarea
            v-if="inspector.type !== 'condition'"
            v-model="inspector.message"
            class="w-full h-24 px-3 py-2 text-sm border rounded-lg outline-none resize-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
          />
          <input
            v-else
            v-model="inspector.condition"
            type="text"
            class="w-full h-9 px-3 text-sm border rounded-lg outline-none bg-n-alpha-1 border-n-weak text-n-slate-12 focus:border-n-blue-9"
          />
        </template>
        <Button
          class="mt-4"
          :label="$t('CONVERSATION_FLOWS.EDITOR.SAVE_NODE')"
          icon="i-lucide-save"
          :disabled="
            inspector.type === 'start' &&
            !isValidWebhookUrl(inspector.config.webhookUrl)
          "
          @click="saveInspector"
        />
      </aside>
    </div>
  </div>
</template>
